declare let gtag: Function;

import { Component, OnInit, HostListener } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { firstValueFrom, of } from 'rxjs';
import { timeout, retry, catchError, filter } from 'rxjs/operators';
import { AuthService } from './services/auth.service';

/** Per-tenant white-label branding from GET /dossier/my-org/branding.
 *  Every field is optional; each falls back to its Irisel default independently. */
interface OrgBranding {
  logo_url?: string | null;
  link_url?: string | null;
  legal_name?: string | null;
  product_name?: string | null;
  terms_url?: string | null;
  site_url?: string | null;
}
import { CryptoService } from './services/crypto.service';
import {
  XmlTreeNode,
  ViewMode,
  getVisibleKeys as _getVisibleKeys,
  isArray as _isArray,
  isObject as _isObject,
  getObjectKeys as _getObjectKeys,
  formatValue as _formatValue,
  parseXmlToTree as _parseXmlToTree,
  isXmlExtractionResult,
  isTableExtractionResult,
  parseCsvTable,
  HIDDEN_KEYS,
} from './utils/extraction-render.utils';

enum ProcessingStep { UPLOAD = 0, CONVERT = 1, OCR = 2, EXTRACT = 3 }
enum DocumentType {
  INVOICE = 'INVOICE', INVOICE_PROVISIONAL = 'INVOICE_PROVISIONAL',
  LETTER_OF_CREDIT = 'LETTER_OF_CREDIT', BILL_OF_LADING = 'BILL_OF_LADING',
  PACKING_LIST = 'PACKING_LIST', COVER_LETTER = 'COVER_LETTER',
  CERTIFICATE_INSURANCE = 'CERTIFICATE_INSURANCE', SWIFT_700 = 'SWIFT_700',
  MEDICAL_RECORD = 'MEDICAL_RECORD', LOAN_APPLICATION = 'LOAN_APPLICATION',
  RECEIPT = 'RECEIPT', PURCHASE_ORDER = 'PURCHASE_ORDER',
  SERVICE_CONTRACT = 'SERVICE_CONTRACT', INSURANCE_CLAIM = 'INSURANCE_CLAIM',
  CURRICULUM_VITAE = 'CURRICULUM_VITAE', CUSTOMS_DECLARATION = 'CUSTOMS_DECLARATION',
  TABLE = 'TABLE',
  UNKNOWN = 'UNKNOWN', AUTO = 'AUTO'
}
enum Language { EN = 'EN', AUTO = 'AUTO' }

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {
  ProcessingStep = ProcessingStep;
  ViewMode = ViewMode;
  DocumentType = DocumentType;
  Language = Language;

  /** True when the current route is /digitize — shows the existing workflow inline */
  isDigitizeRoute = false;

  // Default branding. A tenant with no org_branding row keeps all of these,
  // which is what makes dd.irisel.com Irisel-branded for self-service users
  // while a white-labelled org sees its own brand on the same deployment.
  readonly defaultLogoUrl = 'https://www.irisel.com/images/irisel-arco-xx-small.png';
  readonly defaultLogoLink = 'https://www.irisel.com';
  readonly defaultLegalName = 'Irisel Consulting SLU';
  readonly defaultTermsUrl = 'https://www.irisel.com';

  /** Tenant logo, resolved from the caller's org after sign-in. */
  headerLogoUrl: string = this.defaultLogoUrl;
  /** Where the header logo links. Null renders the logo unlinked — a tenant's
   *  own mark should not send their users to irisel.com. */
  headerLogoLink: string | null = this.defaultLogoLink;
  /** Copyright holder shown in About. */
  brandLegalName: string = this.defaultLegalName;
  /** Target of the About dialog's terms link. */
  brandTermsUrl: string = this.defaultTermsUrl;
  /** Set when the tenant renames the product; overrides the translated title. */
  brandProductName: string | null = null;

  currentStep = ProcessingStep.UPLOAD;
  jobName = '';
  jobUuid = '';  // Store job UUID for security validation
  hasEmbeddedText = false;  // Store text detection result
  forceOcr = false;  // Force OCR even for text-based PDFs
  ocrMethod = '';  // Store OCR method: 'ocr' or 'text_extraction'
  error = '';
  result: any = null;
  viewMode: ViewMode = ViewMode.TREE;
  extractionFeedback: 'up' | 'down' | null = null;
  csvTable: { headers: string[], rows: string[][] } | null = null;
  uploadedFile: File | null = null;
  imageUrls: string[] = [];
  showFirstTimeHint: boolean = typeof localStorage !== 'undefined' && localStorage.getItem('dd_first_upload_done') !== '1';

  dismissFirstTimeHint(): void {
    this.showFirstTimeHint = false;
    try { localStorage.setItem('dd_first_upload_done', '1'); } catch {}
  }
  isProcessing = false;
  ocrResult: any = null;

  // Timeouts (milliseconds) — loaded from /config endpoint, no hardcoded defaults
  timeoutUpload = 0;
  timeoutConvert = 0;
  timeoutOcr = 0;
  timeoutExtract = 0;

  // Billing config — loaded from /config endpoint
  billingBaseTokensPerPage = 0;
  billingMarkup = 0;
  billingTokenValueUsd = 0;

  // PayPal config — loaded from /config endpoint
  paypalHostedButtonId = '';

  // Paywall
  showPaywall = false;
  paywallMinBalance = 1000;

  get isPaywalled(): boolean {
    const balance = this.auth.user?.balance ?? 0;
    return balance < this.paywallMinBalance;
  }

  // Payment result message (shown after PayPal redirect)
  paymentMessage: { icon: string; titleKey: string; textKey: string; color: string } | null = null;

  elapsedTimes: Record<string, number | null> = { upload: null, convert: null, ocr: null, extract: null };
  private _timerStart = 0;
  private _timerInterval: any = null;
  elapsedDisplay = '';

  private startTimer(): void {
    this._timerStart = Date.now();
    this.elapsedDisplay = '0s';
    this._timerInterval = setInterval(() => {
      this.elapsedDisplay = this.formatElapsed(Date.now() - this._timerStart);
    }, 500);
  }

  private stopTimer(stepKey: string): void {
    clearInterval(this._timerInterval);
    this._timerInterval = null;
    const ms = Date.now() - this._timerStart;
    this.elapsedTimes[stepKey] = ms;
    this.elapsedDisplay = this.formatElapsed(ms);
  }

  formatElapsed(ms: number): string {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
  }

  selectedDocumentType: string = DocumentType.UNKNOWN;
  selectedLanguage: Language = Language.AUTO;
  selectedModel = '';
  usedModel = '';
  selectedDpi = 100;
  selectedOutputFormat = 'json';
  defaultOcrBackend: 'textract' | 'paddle' = 'paddle';  // Set from /config defaults
  ocrBackend: 'textract' | 'paddle' = 'paddle';  // Overridden by /config defaults
  ocrCpuThreads = 4;  // Increased from 1 to 4 for better performance
  ocrEnableMkldnn = false;
  ocrMaxWorkers = 4;  // Increased from 1 to 4 for parallel processing

  // Backend provider filter for model selection
  selectedBackend: 'openai' | 'ollama' | 'bedrock' | 'local' = 'openai';  // Overridden by /config defaults

  // Schema source: 'internal' (Pydantic) or 'external' (XSD)
  selectedSchemaSource: 'internal' | 'external' = 'internal';

  // Categorized document types from API
  internalDocumentTypes: string[] = Object.values(DocumentType);
  externalDocumentTypes: string[] = [];

  // Output formats per document type from API
  outputFormatsMap: Record<string, string[]> = {
    default: ['json', 'xml'],
    TABLE: ['json', 'xml', 'csv', 'odf', 'xlsx'],
  };

  dpiOptions = [
    { label: 'Very Low-Res (100 DPI) — fastest, basic quality', value: 100 },
    { label: 'Low-Res (150 DPI) — fast, good for clean documents', value: 150 },
    { label: 'Standard (200 DPI) — balanced speed and quality', value: 200 },
    { label: 'High-Res (300 DPI) — slower, best quality', value: 300 },
  ];
  ocrThreadOptions = [
    { label: '1 thread', value: 1 }, { label: '2 threads', value: 2 },
    { label: '4 threads', value: 4 }, { label: '8 threads', value: 8 },
    { label: '16 threads', value: 16 },
  ];
  ocrMaxWorkersOptions = [1, 2, 4, 8];
  documentTypes: string[] = Object.values(DocumentType);  // fallback until API responds
  languages = Object.values(Language);
  modelOptions: string[] = environment.modelOptions;  // fallback until API responds

  stepsCompleted = { upload: false, convert: false, ocr: false, extract: false };

  private apiUrl = environment.apiBaseUrl;
  private staticUrl = environment.staticBaseUrl;
  appVersion = environment.version;
  gitCommit = environment.gitCommit;

  user: any = null;
  showUserMenu = false;

  // Help dialog
  showHelpDialog = false;
  helpEmail = '';
  helpMessage = '';
  helpSending = false;
  helpError = '';
  helpEmailWarning = '';
  helpStatus: 'idle' | 'success' = 'idle';

  // Language switching
  currentLanguage = 'en';
  availableLanguages: Array<{ code: string; label: string; flag: string }> = [];
  showLanguageDropdown = false;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    public auth: AuthService,
    private cryptoService: CryptoService,
    private router: Router,
  ) {
    // Detect /digitize route to show existing workflow inline
    this.isDigitizeRoute = this.router.url === '/digitize';
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe((e: NavigationEnd) => {
      this.isDigitizeRoute = e.urlAfterRedirects === '/digitize';
      // Refresh balance on every navigation (dossier pipeline deducts tokens server-side)
      this.auth.refreshProfile();
      // Track page views in GA4 with specialized event per view
      if (typeof gtag === 'function') {
        const url = e.urlAfterRedirects;
        let viewEvent = 'view_other';
        if (url === '/' || url === '') {
          viewEvent = 'view_home';
        } else if (url === '/digitize') {
          viewEvent = 'view_digitize';
        } else if (url === '/dossiers') {
          viewEvent = 'view_dossier_list';
        } else if (/^\/dossiers\/[^/]+\/uploads\/[^/]+\/documents\/[^/]+\/extract$/.test(url)) {
          viewEvent = 'view_extraction_review';
        } else if (/^\/dossiers\/[^/]+\/uploads\/[^/]+\/extraction-review$/.test(url)) {
          viewEvent = 'view_extraction_summary';
        } else if (/^\/dossiers\/[^/]+\/uploads\/[^/]+\/review$/.test(url)) {
          viewEvent = 'view_upload_review';
        } else if (/^\/dossiers\/[^/]+$/.test(url)) {
          viewEvent = 'view_dossier_detail';
        }
        gtag('event', viewEvent, { page_path: url });
      }
    });

    // Initialize translation service with English as fallback
    this.translate.setDefaultLang('en');
    this.translate.use('en');
    
    // Load available languages, then auto-select based on browser locale
    this.http.get<Array<{ code: string; label: string; flag: string }>>('assets/i18n/languages.json')
      .subscribe(
        languages => {
          this.availableLanguages = languages;
          const availableCodes = languages.map(l => l.code);
          const browserLang = this.detectBrowserLanguage(availableCodes);
          this.currentLanguage = browserLang;
          this.translate.use(browserLang);
        },
        error => {
          console.error('Failed to load language configuration:', error);
          this.availableLanguages = [
            { code: 'en', label: 'English', flag: 'gb' },
            { code: 'es', label: 'Español', flag: 'es' }
          ];
        }
      );
  }

  /** Detect best matching language from browser settings against available codes. */
  private detectBrowserLanguage(availableCodes: string[]): string {
    const browserLangs = navigator.languages || [navigator.language];
    for (const lang of browserLangs) {
      // Exact match (e.g. "pt-BR")
      if (availableCodes.includes(lang)) return lang;
      // Base language match (e.g. "pt-BR" → "pt-BR", "en-US" → "en")
      const base = lang.split('-')[0];
      const match = availableCodes.find(c => c === base || c.startsWith(base + '-'));
      if (match) return match;
    }
    return 'en';
  }

  ngOnInit(): void {
    this.exposeTheme();
    this.exposeKeyMgmt();
    this.exposeBranding();
    // Model list comes from environment.modelOptions (includes pricing).
    // Server-side ALLOWED_MODELS is used only for validation, not UI population.

    // Fetch runtime configuration (timeouts, billing, etc.)
    this.http.get<{ timeouts: Record<string, number>; billing?: Record<string, number>; paypal?: Record<string, string> }>(`${this.apiUrl}/config`).subscribe({
      next: res => {
        if (res.timeouts) {
          this.timeoutUpload = res.timeouts['upload'] || 0;
          this.timeoutConvert = res.timeouts['convert'] || 0;
          this.timeoutOcr = res.timeouts['ocr'] || 0;
          this.timeoutExtract = res.timeouts['extract'] || 0;
        }
        if (res.billing) {
          this.billingBaseTokensPerPage = res.billing['base_tokens_per_page'] || 0;
          this.billingMarkup = res.billing['markup'] || 0;
          this.billingTokenValueUsd = res.billing['token_value_usd'] || 0;
        }
        if (res.paypal) {
          this.paypalHostedButtonId = res.paypal['hosted_button_id'] || '';
        }
        const defaults = (res as any).defaults;
        if (defaults) {
          if (defaults.model) this.selectedModel = defaults.model;
          if (defaults.ocr_backend) { this.ocrBackend = defaults.ocr_backend; this.defaultOcrBackend = defaults.ocr_backend; }
          if (defaults.extract_backend) this.selectedBackend = defaults.extract_backend;
        }
      },
      error: () => { logger: console.warn('Failed to load config from API'); }
    });

    // Fetch available document types (internal Pydantic + external XSD)
    this.http.get<{ internal: string[]; external: string[]; document_types: string[]; output_formats?: Record<string, string[]> }>(`${this.apiUrl}/document_types`).subscribe({
      next: res => {
        if (res.internal?.length) this.internalDocumentTypes = res.internal;
        if (res.external?.length) this.externalDocumentTypes = res.external;
        if (res.document_types?.length) this.documentTypes = res.document_types;
        if (res.output_formats) this.outputFormatsMap = res.output_formats;
      },
      error: () => { /* keep enum fallback */ }
    });

    // Check authentication session on app load
    this.auth.checkSession().subscribe(user => {
      this.user = user;
      this.loadOrgBranding();
    });

    // Subscribe to user changes
    this.auth.user$.subscribe(user => {
      this.user = user;
      this.loadOrgBranding();
    });

    // Handle PayPal return — if URL contains payment params, process and clean up
    this.handlePaymentReturn();
  }

  /** Resolve the tenant's header logo. Silently keeps the Irisel default when
   *  the user has no org, the org has set no branding, or the call fails —
   *  branding must never be able to break the header. */
  private loadOrgBranding(): void {
    if (!this.user?.org_group) {
      this.resetBrandingToDefault();
      return;
    }
    this.http.get<OrgBranding>(
      `${environment.apiBaseUrl}/dossier/my-org/branding`,
      { withCredentials: true }
    ).pipe(catchError(() => of({} as OrgBranding)))
     .subscribe(branding => {
       this.headerLogoUrl = branding.logo_url || this.defaultLogoUrl;
       this.headerLogoLink = branding.logo_url
         ? (branding.link_url ?? null)  // tenant logo: their link, or unlinked
         : this.defaultLogoLink;        // default logo: always links to Irisel
       this.brandLegalName = branding.legal_name || this.defaultLegalName;
       this.brandTermsUrl = branding.terms_url || branding.site_url || this.defaultTermsUrl;
       this.brandProductName = branding.product_name || null;
     });
  }

  /** Restore every branding element to the Irisel default. */
  private resetBrandingToDefault(): void {
    this.headerLogoUrl = this.defaultLogoUrl;
    this.headerLogoLink = this.defaultLogoLink;
    this.brandLegalName = this.defaultLegalName;
    this.brandTermsUrl = this.defaultTermsUrl;
    this.brandProductName = null;
  }

  /** Fall back to the Irisel logo when the tenant's image fails to load.
   *  Only the logo reverts — the tenant's white-label text still applies. */
  onHeaderLogoError(): void {
    this.headerLogoUrl = this.defaultLogoUrl;
    this.headerLogoLink = this.defaultLogoLink;
  }

  /** Handle return from PayPal payment — show result message with Close button. */
  private handlePaymentReturn(): void {
    const params = new URLSearchParams(window.location.search);
    const paymentResult = params.get('payment');
    if (!paymentResult) return;

    // Clean the URL (remove payment query param)
    window.history.replaceState({}, document.title, window.location.pathname);

    if (paymentResult === 'success') {
      this.paymentMessage = {
        icon: '✅',
        titleKey: 'payment.successTitle',
        textKey: 'payment.successText',
        color: '#2e7d32',
      };
      if (typeof gtag === 'function') {
        gtag('event', 'payment_success');
      }
    } else if (paymentResult === 'cancelled') {
      this.paymentMessage = {
        icon: '⚠️',
        titleKey: 'payment.cancelledTitle',
        textKey: 'payment.cancelledText',
        color: '#e65100',
      };
      if (typeof gtag === 'function') {
        gtag('event', 'payment_cancelled');
      }
    }
  }

  dismissPaymentMessage(): void {
    this.paymentMessage = null;
  }

  login(): void {
    this.auth.login();
  }

  loginWithGoogle(): void {
    this.auth.loginWithGoogle();
  }

  register(): void {
    if (typeof gtag === 'function') {
      gtag('event', 'registration_initiated', { event_category: 'engagement' });
    }
    this.auth.register();
  }

  registerWithGoogle(): void {
    if (typeof gtag === 'function') {
      gtag('event', 'registration_initiated', { event_category: 'engagement' });
    }
    this.auth.registerWithGoogle();
  }

  logout(): void {
    this.auth.logout();
  }

  userInitials(): string {
    if (!this.user?.name) return '?';
    const parts = this.user.name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return this.user.name[0]?.toUpperCase() || '?';
  }

  formatDocumentType(type: string): string {
    const translated = this.translate.instant('documentTypes.' + type);
    if (translated !== 'documentTypes.' + type) {
      return translated;
    }
    return type;
  }

  /** Returns the document type list for the currently selected schema source. */
  get activeDocumentTypes(): string[] {
    return this.selectedSchemaSource === 'external'
      ? this.externalDocumentTypes
      : this.internalDocumentTypes;
  }

  /** Returns the effective document_type to send to the API. */
  get effectiveDocumentType(): string {
    if (this.selectedSchemaSource === 'external') {
      return 'xsd/' + this.selectedDocumentType;
    }
    return this.selectedDocumentType;
  }

  /** Returns the available output formats for the currently selected document type. */
  get activeOutputFormats(): string[] {
    return this.outputFormatsMap[this.selectedDocumentType] || this.outputFormatsMap['default'] || ['json', 'xml'];
  }

  /** Returns true if the current result is a table format (CSV, ODF, XLSX). */
  isTableResult(): boolean {
    return this.result?.format && ['csv', 'odf', 'xlsx'].includes(this.result.format);
  }

  /** Returns models filtered by the selected backend provider. */
  get filteredModelOptions(): string[] {
    switch (this.selectedBackend) {
      case 'ollama':
        return this.modelOptions.filter(m => m.startsWith('ollama/'));
      case 'bedrock':
        return this.modelOptions.filter(m => m.startsWith('bedrock/'));
      case 'local':
        return this.modelOptions.filter(m => m.startsWith('local/'));
      case 'openai':
      default:
        return this.modelOptions.filter(m => !m.startsWith('ollama/') && !m.startsWith('bedrock/') && !m.startsWith('local/'));
    }
  }

  /** Strip provider prefix from model name for display (e.g. 'ollama/gemma3:27b' → 'gemma3:27b'). */
  displayModel(m: string): string {
    const slash = m.indexOf('/');
    return slash >= 0 ? m.substring(slash + 1) : m;
  }

  /** When backend changes, reset selectedModel to first available option. */
  onBackendChange(): void {
    const filtered = this.filteredModelOptions;
    if (filtered.length && !filtered.includes(this.selectedModel)) {
      this.selectedModel = filtered[0];
    }
  }

  /** When schema source changes, reset selectedDocumentType to first available. */
  onSchemaSourceChange(): void {
    const types = this.activeDocumentTypes;
    if (types.length && !types.includes(this.selectedDocumentType)) {
      this.selectedDocumentType = types[0];
    }
    this.resetOutputFormatIfNeeded();
  }

  /** When document type changes, reset output format if current is not available. */
  onDocumentTypeChange(): void {
    this.resetOutputFormatIfNeeded();
    if (typeof gtag === 'function') {
      gtag('event', 'document_type_selected', { document_type: this.selectedDocumentType });
    }
  }

  sendExtractionFeedback(rating: 'up' | 'down'): void {
    this.extractionFeedback = rating;
    if (typeof gtag === 'function') {
      gtag('event', 'extraction_feedback', {
        rating,
        document_type: this.selectedDocumentType,
      });
    }
  }

  /** Reset selectedOutputFormat to first available if current is not in the active list. */
  private resetOutputFormatIfNeeded(): void {
    const formats = this.activeOutputFormats;
    if (!formats.includes(this.selectedOutputFormat)) {
      this.selectedOutputFormat = formats[0] || 'json';
    }
  }

  isXmlResult(): boolean {
    return this.selectedOutputFormat === 'xml' || this.selectedSchemaSource === 'external';
  }

  /** Returns true when the result is a non-TABLE CSV extraction (result.csv string present). */
  isCsvResult(): boolean {
    return this.selectedOutputFormat === 'csv' && !!this.result?.csv;
  }

  /** Parsed XML tree for tree view rendering. Recomputed when result changes. */
  parsedXmlTree: XmlTreeNode | null = null;

  /** Parse XML string into a tree structure for rendering. */
  parseXmlToTree(xml: string): XmlTreeNode | null {
    return _parseXmlToTree(xml);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    const isPdfExt = /\.pdf$/i.test(file.name);
    const isImgExt = /\.(jpe?g|png)$/i.test(file.name);
    const isPdfMime = !file.type || file.type === 'application/pdf';
    const isImgMime = !file.type || /^image\/(jpeg|png)$/.test(file.type);
    const ok = (isPdfExt && isPdfMime) || (isImgExt && isImgMime);
    if (!ok) {
      this.error = 'Only PDF, JPG, or PNG files are allowed.';
      input.value = '';
      return;
    }
    this.uploadedFile = file;
    this.jobName = file.name.replace(/\.(pdf|jpe?g|png)$/i, '');
    this.error = '';
    input.value = '';
  }

  async nextStep(): Promise<void> {
    if (this.currentStep === ProcessingStep.UPLOAD) {
      await this.executeUpload();
      if (!this.error) {
        this.currentStep = ProcessingStep.CONVERT;
        if (!this.stepsCompleted.convert) await this.executeConvert();
      }
    } else if (this.currentStep === ProcessingStep.CONVERT) {
      this.currentStep = ProcessingStep.OCR;
      if (!this.stepsCompleted.ocr) await this.executeOCR();
    } else if (this.currentStep === ProcessingStep.OCR) {
      this.currentStep = ProcessingStep.EXTRACT;
      // Don't auto-execute extraction - let user select model first
    }
  }

  async repeatStep(): Promise<void> {
    const isExtractFirstRun =
      this.currentStep === ProcessingStep.EXTRACT && !this.stepsCompleted.extract;
    if (!isExtractFirstRun && typeof gtag === 'function') {
      const stepEvents: Record<number, string> = {
        [ProcessingStep.CONVERT]: 'repeat_convert',
        [ProcessingStep.OCR]:     'repeat_ocr',
        [ProcessingStep.EXTRACT]: 'repeat_extract',
      };
      gtag('event', stepEvents[this.currentStep] || 'repeat_other');
    }
    switch (this.currentStep) {
      case ProcessingStep.CONVERT: await this.executeConvert(); break;
      case ProcessingStep.OCR:     await this.executeOCR();     break;
      case ProcessingStep.EXTRACT: await this.executeExtract(); break;
    }
  }

  async executeUpload(): Promise<void> {
    if (!this.uploadedFile) return;
    this.isProcessing = true;
    this.error = '';
    this.startTimer();
    try {
      const formData = new FormData();
      formData.append('file', this.uploadedFile);
      const response: any = await this.http.post(`${this.apiUrl}/upload`, formData)
        .pipe(timeout(this.timeoutUpload), retry(2))
        .toPromise();
      
      // Store job UUID from response
      if (response?.job_uuid) {
        this.jobUuid = response.job_uuid;
      }
      
      // Store text detection result
      if (response?.has_embedded_text !== undefined) {
        this.hasEmbeddedText = response.has_embedded_text;
      }
      
      this.stepsCompleted.upload = true;
      this.dismissFirstTimeHint();
      if (response?.is_image) {
        this.stepsCompleted.convert = true;
        this.imageUrls = [
          `${this.apiUrl}/image?job=${encodeURIComponent(this.jobName)}&job_uuid=${encodeURIComponent(this.jobUuid)}&page=0`
        ];
      }
      if (typeof gtag === 'function') {
        gtag('event', 'dd_upload', { event_category: 'digitize_flow' });
      }
    } catch (err: any) {
      // Extract detailed error message from backend if available
      this.error = err.error?.detail || err.message || 'Upload failed';
      if (typeof gtag === 'function') {
        gtag('event', 'dd_error', { event_category: 'digitize_flow', step: 'upload', error_message: (this.error || '').substring(0, 100) });
      }
    } finally {
      this.stopTimer('upload');
      this.isProcessing = false;
    }
  }

  async executeConvert(): Promise<void> {
    if (!this.jobName) return;
    this.isProcessing = true;
    this.error = '';
    this.imageUrls = [];
    this.startTimer();
    try {
      const params = new URLSearchParams({
        job: this.jobName,
        dpi: this.selectedDpi.toString(),
      });
      if (this.jobUuid) {
        params.append('job_uuid', this.jobUuid);
      }
      
      const response: any = await this.http.post(
        `${this.apiUrl}/pdf2img?${params.toString()}`, {}
      ).pipe(timeout(this.timeoutConvert), retry(2),
        catchError(err => { throw new Error(`PDF conversion failed: ${err.message}`); }))
        .toPromise();
      this.stepsCompleted.convert = true;
      if (typeof gtag === 'function') {
        gtag('event', 'dd_convert', { event_category: 'digitize_flow' });
      }
      if (response?.pages) {
        this.imageUrls = Array.from({ length: response.pages }, (_, i) =>
          `${this.apiUrl}/image?job=${encodeURIComponent(this.jobName)}&job_uuid=${encodeURIComponent(this.jobUuid)}&page=${i}`
        );
      }
    } catch (err: any) {
      this.error = err.message || 'Conversion failed';
      if (typeof gtag === 'function') {
        gtag('event', 'dd_error', { event_category: 'digitize_flow', step: 'convert', error_message: (this.error || '').substring(0, 100) });
      }
    } finally {
      this.stopTimer('convert');
      this.isProcessing = false;
    }
  }

  async executeOCR(): Promise<void> {
    if (!this.jobName) return;
    this.isProcessing = true;
    this.error = '';
    this.ocrResult = null;
    this.startTimer();
    try {
      const params = new URLSearchParams({
        job: this.jobName,
        cpu_threads: this.ocrCpuThreads.toString(),
        enable_mkldnn: this.ocrEnableMkldnn.toString(),
        max_workers: this.ocrMaxWorkers.toString(),
        force_ocr: this.forceOcr.toString(),
      });
      if (this.jobUuid) {
        params.append('job_uuid', this.jobUuid);
      }
      // Add OCR backend override (only sent if user is developer)
      if (this.isDeveloper()) {
        params.append('ocr_backend', this.ocrBackend);
      }
      
      const response: any = await this.http.post(`${this.apiUrl}/ocr?${params.toString()}`, {})
        .pipe(timeout(this.timeoutOcr), retry(1),
          catchError(err => { throw new Error(`OCR processing failed: ${err.message}`); }))
        .toPromise();
      this.stepsCompleted.ocr = true;
      this.ocrResult = response;
      this.ocrMethod = response.method || 'ocr';  // Store method: 'ocr', 'textract', 'paddle', or 'text_extraction'
      this.auth.refreshProfile();
      if (typeof gtag === 'function') {
        gtag('event', 'dd_ocr', { event_category: 'digitize_flow', ocr_method: this.ocrMethod });
      }

      // Auto-classify document type after OCR
      this.classifyDocument();
    } catch (err: any) {
      this.error = err.message || 'OCR failed';
      if (typeof gtag === 'function') {
        gtag('event', 'dd_error', { event_category: 'digitize_flow', step: 'ocr', error_message: (this.error || '').substring(0, 100) });
      }
    } finally {
      this.stopTimer('ocr');
      this.isProcessing = false;
    }
  }

  async executeOCRSimple(): Promise<void> {
    if (!this.jobName) return;
    this.isProcessing = true;
    this.error = '';
    this.ocrResult = null;
    this.startTimer();
    try {
      const params = new URLSearchParams({ job: this.jobName });
      if (this.jobUuid) {
        params.append('job_uuid', this.jobUuid);
      }
      
      const response: any = await this.http.post(`${this.apiUrl}/ocr_simple?${params.toString()}`, {})
        .pipe(timeout(this.timeoutOcr), retry(1),
          catchError(err => { throw new Error(`Failsafe OCR failed: ${err.message}`); }))
        .toPromise();
      this.stepsCompleted.ocr = true;
      this.ocrResult = response;
      this.auth.refreshProfile();
      if (typeof gtag === 'function') {
        gtag('event', 'dd_ocr', { event_category: 'digitize_flow', ocr_method: 'simple' });
      }

      // Auto-classify document type after OCR
      this.classifyDocument();
    } catch (err: any) {
      this.error = err.message || 'Failsafe OCR failed';
      if (typeof gtag === 'function') {
        gtag('event', 'dd_error', { event_category: 'digitize_flow', step: 'ocr_simple', error_message: (this.error || '').substring(0, 100) });
      }
    } finally {
      this.stopTimer('ocr');
      this.isProcessing = false;
    }
  }

  /** Call /classify after OCR to auto-detect document type and preset the dropdown. */
  private classifyDocument(): void {
    const params = new URLSearchParams({ job: this.jobName });
    if (this.jobUuid) {
      params.append('job_uuid', this.jobUuid);
    }
    this.http.post<{ type: string; confidence: number; available_types: string[] }>(
      `${this.apiUrl}/classify?${params.toString()}`, {}
    ).subscribe({
      next: res => {
        if (!res.type || res.type === 'UNKNOWN' || res.type === 'CONTINUE') return;

        // Determine schema source and type name from the classified type
        if (res.type.startsWith('xsd/')) {
          const xsdName = res.type.substring(4);
          if (this.externalDocumentTypes.includes(xsdName)) {
            this.selectedSchemaSource = 'external';
            this.selectedDocumentType = xsdName;
          }
        } else if (this.internalDocumentTypes.includes(res.type)) {
          this.selectedSchemaSource = 'internal';
          this.selectedDocumentType = res.type;
        }
        this.resetOutputFormatIfNeeded();
      },
      error: err => {
        // Classification is best-effort — don't block the user
        console.warn('Document classification failed:', err.message);
      }
    });
  }

  async executeExtract(): Promise<void> {
    if (!this.jobName) return;
    this.isProcessing = true;
    this.error = '';
    this.result = null;
    this.startTimer();
    try {
      this.usedModel = this.selectedModel;
      const params = new URLSearchParams({
        job: this.jobName,
        document_type: this.effectiveDocumentType,
        language: this.selectedLanguage,
        model: this.selectedModel,
        outfmt: this.selectedOutputFormat,
        channel: 'dd',
      });
      if (this.jobUuid) {
        params.append('job_uuid', this.jobUuid);
      }

      const extractUrl = `${this.apiUrl}/extract?${params.toString()}`;
      this.result = await this.http.post(extractUrl, {})
        .pipe(timeout(this.timeoutExtract), retry(2),
          catchError(err => { throw new Error(`Data extraction failed: ${err.error?.detail || err.message}`); }))
        .toPromise();
      this.stepsCompleted.extract = true;
      // Parse XML tree for tree view if result is XML
      if (this.isXmlResult() && this.result?.xml) {
        this.parsedXmlTree = this.parseXmlToTree(this.result.xml);
        this.csvTable = null;
      } else if (this.isCsvResult()) {
        this.parsedXmlTree = null;
        this.csvTable = parseCsvTable(this.result.csv);
        this.viewMode = ViewMode.SPREADSHEET;
      } else {
        this.parsedXmlTree = null;
        this.csvTable = null;
      }
      this.auth.refreshProfile();
      if (typeof gtag === 'function') {
        gtag('event', 'dd_extract', { event_category: 'digitize_flow', document_type: this.effectiveDocumentType, model: this.selectedModel });
      }
    } catch (err: any) {
      this.error = err.message || 'Extraction failed';
      if (typeof gtag === 'function') {
        gtag('event', 'dd_error', { event_category: 'digitize_flow', step: 'extract', error_message: (this.error || '').substring(0, 100) });
      }
    } finally {
      this.stopTimer('extract');
      this.isProcessing = false;
    }
  }

  async executeExtractSmallModel(): Promise<void> {
    if (!this.jobName) return;
    this.isProcessing = true;
    this.error = '';
    this.result = null;
    this.startTimer();
    try {
      this.usedModel = this.selectedModel + ' (small-model)';
      const params = new URLSearchParams({
        job: this.jobName,
        document_type: this.effectiveDocumentType,
        language: this.selectedLanguage,
        model: this.selectedModel,
        use_small_model_extractor: 'true',
        outfmt: this.selectedOutputFormat,
        channel: 'dd',
      });
      if (this.jobUuid) {
        params.append('job_uuid', this.jobUuid);
      }

      const extractUrl = `${this.apiUrl}/extract?${params.toString()}`;
      this.result = await this.http.post(extractUrl, {})
        .pipe(timeout(this.timeoutExtract), retry(0),
          catchError(err => { throw new Error(`Small model extraction failed: ${err.error?.detail || err.message}`); }))
        .toPromise();
      this.stepsCompleted.extract = true;
      if (this.isXmlResult() && this.result?.xml) {
        this.parsedXmlTree = this.parseXmlToTree(this.result.xml);
        this.csvTable = null;
      } else if (this.isCsvResult()) {
        this.parsedXmlTree = null;
        this.csvTable = parseCsvTable(this.result.csv);
        this.viewMode = ViewMode.SPREADSHEET;
      } else {
        this.parsedXmlTree = null;
        this.csvTable = null;
      }
      this.auth.refreshProfile();
      if (typeof gtag === 'function') {
        gtag('event', 'dd_extract', { event_category: 'digitize_flow', document_type: this.effectiveDocumentType, model: this.selectedModel, small_model: true });
      }
    } catch (err: any) {
      this.error = err.message || 'Small model extraction failed';
      if (typeof gtag === 'function') {
        gtag('event', 'dd_error', { event_category: 'digitize_flow', step: 'extract_small', error_message: (this.error || '').substring(0, 100) });
      }
    } finally {
      this.stopTimer('extract');
      this.isProcessing = false;
    }
  }

  canClickNext(): boolean {
    if (this.isProcessing) return false;
    if (this.currentStep === ProcessingStep.UPLOAD) return !!this.uploadedFile;
    if (this.currentStep === ProcessingStep.CONVERT) return this.stepsCompleted.convert;
    if (this.currentStep === ProcessingStep.OCR) return this.stepsCompleted.ocr;
    return false;
  }

  canRepeat(): boolean {
    return !this.isProcessing && this.currentStep !== ProcessingStep.UPLOAD;
  }

  goToStep(step: ProcessingStep): void {
    if (step <= this.currentStep) {
      this.currentStep = step;
      this.error = '';
    }
  }

  canGoToStep(step: ProcessingStep): boolean {
    return step <= this.currentStep;
  }

  reset(): void {
    this.currentStep = ProcessingStep.UPLOAD;
    this.jobName = '';
    this.error = '';
    this.result = null;
    this.ocrResult = null;
    this.viewMode = ViewMode.TREE;
    this.uploadedFile = null;
    this.imageUrls = [];
    this.hasEmbeddedText = false;
    this.forceOcr = false;
    this.parsedXmlTree = null;
    this.csvTable = null;
    this.stepsCompleted = { upload: false, convert: false, ocr: false, extract: false };
    this.elapsedTimes = { upload: null, convert: null, ocr: null, extract: null };
    this.selectedDpi = 150;
    this.ocrBackend = this.defaultOcrBackend;
    this.ocrCpuThreads = 4;  // Increased default
    this.ocrEnableMkldnn = false;
    this.ocrMaxWorkers = 4;  // Increased default
  }

  openImagePopup(url: string, index: number): void {
    window.open(url, `img_${index}`);
  }

  toggleViewMode(): void {
    this.viewMode = this.viewMode === ViewMode.TREE ? ViewMode.JSON : ViewMode.TREE;
  }

  getVisibleKeys(obj: any): string[] {
    return _getVisibleKeys(obj);
  }

  estimatedCost(): string | null {
    const usage = this.result?.token_usage;
    if (!usage) return null;
    const modelKey = this.usedModel.replace(' (small-model)', '');
    const pricing = (environment.modelPricing as any)[modelKey];
    if (!pricing) return null;
    const cost = (usage.prompt_tokens / 1_000_000) * pricing.input
               + (usage.completion_tokens / 1_000_000) * pricing.output;
    return cost < 0.001 ? '< $0.001' : `$${cost.toFixed(4)}`;
  }

  billingTokensDisplay(): string | null {
    const billing = this.result?.billing_tokens;
    if (!billing) return null;
    return `${billing.extraction}`;
  }


  shouldShowCostInfo(): boolean {
    // Show cost info only for logged-in users
    return !!this.user;
  }

  isDeveloper(): boolean {
    return this.auth.isDeveloper;
  }

  private openPopup(name: string, features: string): Window | null {
    let w = window.open('', name, features);
    if (!w) return null;
    try { void w.document.title; } catch {
      // Popup navigated to a cross-origin URL — close and reopen fresh.
      w.close();
      w = window.open('about:blank', name, features);
    }
    return w;
  }

  switchLanguage(lang: string): void {
    this.translate.use(lang);
  }

  toggleLanguageDropdown(): void {
    this.showLanguageDropdown = !this.showLanguageDropdown;
  }

  /** Close dropdowns when clicking outside. */
  @HostListener('document:click')
  onDocumentClick(): void {
    this.showLanguageDropdown = false;
  }

  selectLanguage(lang: { code: string; label: string; flag: string }): void {
    this.currentLanguage = lang.code;
    this.translate.use(lang.code);
    this.showLanguageDropdown = false;
  }

  /** Returns the currently selected language object. */
  get currentLang(): { code: string; label: string; flag: string } | undefined {
    return this.availableLanguages.find(l => l.code === this.currentLanguage);
  }

  private checkPaywall(): boolean {
    const balance = this.auth.user?.balance ?? 0;
    if (balance < this.paywallMinBalance) {
      // Show the paywall modal instead of opening PayPal directly. The modal
      // branches on auth state: authenticated users see the PayPal top-up,
      // anonymous users are prompted to register/login (they cannot pay).
      this.showPaywall = true;
      return false;
    }
    return true;
  }

  openPaypalTopup(): void {
    const url = 'https://www.paypal.com/cgi-bin/webscr'
      + '?cmd=_s-xclick'
      + '&hosted_button_id=' + encodeURIComponent(this.paypalHostedButtonId)
      + '&currency_code=EUR'
      + '&custom=' + encodeURIComponent(this.auth.user?.email || '');
    window.open(url, '_blank');
  }

  downloadResult(): void {
    if (!this.checkPaywall()) return;
    if (this.isCsvResult() && this.result?.csv) {
      const blob = new Blob([this.result.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.jobName}_extraction.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (this.isTableResult() && this.result?.content) {
      const fmt = this.result.format;
      const mimeTypes: Record<string, string> = {
        csv: 'text/csv',
        odf: 'application/vnd.oasis.opendocument.spreadsheet',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      };
      const blob = new Blob([this.result.content], { type: mimeTypes[fmt] || 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.jobName}_table.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (this.isXmlResult() && this.result?.xml) {
      const blob = new Blob([this.result.xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.jobName}_extraction.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const json = JSON.stringify(this.result, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.jobName}_extraction.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }

  /** Download the OCR text as a plain .txt file, page-separated. Paywall-gated. */
  downloadOcrText(): void {
    if (!this.checkPaywall()) return;
    const pages = this.ocrResult?.results;
    if (!Array.isArray(pages) || pages.length === 0) return;

    const parts: string[] = [];
    for (const page of pages) {
      const label = page.page != null ? page.page : '';
      parts.push(`--- Page ${label} ---`);
      // Prefer the backend's box-reconstructed text; fall back to token join.
      const text: string = typeof page.text === 'string' && page.text.length > 0
        ? page.text
        : (Array.isArray(page.rec_texts) ? page.rec_texts.join('\n') : '');
      parts.push(text);
      parts.push('');
    }
    const blob = new Blob([parts.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.jobName}_ocr.txt`;
    a.click();
    URL.revokeObjectURL(url);
    if (typeof gtag === 'function') {
      gtag('event', 'dd_ocr_download', { event_category: 'digitize_flow', format: 'txt' });
    }
  }

  copyResult(): void {
    if (!this.checkPaywall()) return;
    if (!this.result) return;
    if (this.isCsvResult() && this.result?.csv) {
      navigator.clipboard.writeText(this.result.csv);
    } else if (this.isTableResult() && this.result?.content) {
      navigator.clipboard.writeText(this.result.content);
    } else if (this.isXmlResult() && this.result?.xml) {
      navigator.clipboard.writeText(this.result.xml);
    } else {
      const json = JSON.stringify(this.result, null, 2);
      navigator.clipboard.writeText(json);
    }
  }

  modelPriceLabel(model: string): string {
    const p = (environment.modelPricing as any)[model];
    if (!p || !this.billingTokenValueUsd || !this.billingMarkup) return '';
    // Show billing tokens per 1M LLM tokens (input/output)
    const inputRate = (p.input * this.billingMarkup) / this.billingTokenValueUsd;
    const outputRate = (p.output * this.billingMarkup) / this.billingTokenValueUsd;
    return `${inputRate.toFixed(0)}/${outputRate.toFixed(0)} ꝟ/1M`;
  }


  showValidationReport(errors: string): void {
    const w = this.openPopup('validation_report', 'width=720,height=500,scrollbars=yes,resizable=yes');
    if (!w) return;
    const escaped = errors.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    w.document.write(
      '<!DOCTYPE html><html><head><title>XSD Validation Report</title>' +
      '<style>body{font-family:monospace;font-size:13px;padding:16px;background:#f9f9f9;color:#333;}' +
      'h2{font-family:sans-serif;font-size:16px;margin:0 0 12px;}' +
      'pre{white-space:pre-wrap;word-break:break-word;background:#fff;border:1px solid #ddd;padding:12px;border-radius:4px;}</style>' +
      '</head><body><h2>XSD Validation Report</h2><pre>' + escaped + '</pre></body></html>'
    );
    w.document.close();
  }

  /** Initialise theme from localStorage and expose window.ddTheme for the account popup. */
  private exposeTheme(): void {
    const saved = localStorage.getItem('dd-theme');
    if (saved === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    }

    (window as any).ddTheme = {
      get: (): string => document.documentElement.getAttribute('data-theme') || 'default',
      set: (theme: string): void => {
        if (theme === 'dark') {
          document.documentElement.setAttribute('data-theme', 'dark');
        } else {
          document.documentElement.removeAttribute('data-theme');
        }
        localStorage.setItem('dd-theme', theme);
      },
    };
  }

  /** Expose window.ddBranding so the account popup's Organization tab can
   *  refresh the main window's header/About branding after an admin changes it —
   *  otherwise the popup's own save succeeds but the header stays stale until
   *  the next sign-in. */
  private exposeBranding(): void {
    (window as any).ddBranding = {
      refresh: (): void => this.loadOrgBranding(),
    };
  }

  /**
   * Expose crypto + key-management operations on window.ddKeyMgmt so the
   * account settings popup (plain JS, no Angular) can call them.
   */
  private exposeKeyMgmt(): void {
    const crypto = this.cryptoService;
    const apiUrl = this.apiUrl;
    const translate = this.translate;

    (window as any).ddShowMyAccount = (tab?: string) => this.showMyAccount(tab);

    (window as any).ddKeyMgmt = {

      /** Generate a server-side keypair encrypted with the given password. */
      createKeypair: (password: string): Promise<any> =>
        firstValueFrom(this.auth.createKeypair(password)),

      /**
       * Generate a new RSA-4096 keypair, encrypt with the user's current password,
       * and POST to /account/keypair/rotate.
       * Returns the retiring-slot key material + new public key for the re-wrap loop.
       */
      generateAndRotate: async (password: string) => {
        // Fetch current keypair data BEFORE rotation (active slot will become retiring)
        const [statusResp, dataResp] = await Promise.all([
          fetch(`${apiUrl}/account/keypair`, { credentials: 'include' }),
          fetch(`${apiUrl}/account/keypair-data`, { credentials: 'include' }),
        ]);
        if (!statusResp.ok || !dataResp.ok) throw new Error('Failed to fetch keypair data');
        const keypairStatus = await statusResp.json();
        const keypairData   = await dataResp.json();
        const activeSlot: string = keypairStatus.key_active_slot || 'A';
        const retiringSlotData   = activeSlot === 'B' ? keypairData.slot_b : keypairData.slot_a;

        // Generate new RSA-4096 keypair in the browser
        const { publicKeyPem, privateKeyPem } = await crypto.generateKeypairBrowser();

        // Encrypt new private key with the user's current password
        const encryptedKey = await crypto.encryptPrivateKeyForStorage(privateKeyPem, password);

        // Initiate rotation on the server
        const rotResp = await fetch(`${apiUrl}/account/keypair/rotate`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            public_key_pem: publicKeyPem,
            encrypted_private_key: encryptedKey.encrypted_private_key,
            private_key_salt: encryptedKey.private_key_salt,
            private_key_nonce: encryptedKey.private_key_nonce,
          }),
        });
        if (!rotResp.ok) { const e = await rotResp.json(); throw new Error(e.detail || 'Rotation failed'); }
        const rotData = await rotResp.json();

        return {
          retiringSlot: rotData.retiring_slot,
          retiringSlotData,
          newPublicKeyPem: publicKeyPem,
          dossiersPending: rotData.dossiers_pending,
        };
      },

      /**
       * Re-wrap a single dossier's DEK from the retiring slot to the new slot.
       * Decrypt the retiring private key → unwrap DEK → re-encrypt with new public key → PATCH.
       */
      rewrapDossier: async (
        dossierId: string,
        encryptedDek: string,
        password: string,
        retiringSlotData: { encrypted_private_key: string; private_key_salt: string; private_key_nonce: string },
        newPublicKeyPem: string,
      ) => {
        const derivedKey = await crypto.deriveKeyFromPassword(password, retiringSlotData.private_key_salt);
        const privateKey = await crypto.decryptPrivateKey(derivedKey, retiringSlotData.encrypted_private_key, retiringSlotData.private_key_nonce);
        const dekHex     = await crypto.unwrapDek(privateKey, encryptedDek);
        const newDek     = await crypto.wrapDekWithPublicKey(dekHex, newPublicKeyPem);

        const resp = await fetch(`${apiUrl}/account/keypair/rotate/dossier/${dossierId}`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ encrypted_dek: newDek }),
        });
        if (!resp.ok) { const e = await resp.json(); throw new Error(e.detail || 'Re-wrap failed'); }
        return await resp.json(); // { remaining, rotation_status }
      },

      /**
       * Re-encrypt all populated keypair slots with a new password and PUT to server.
       * Blocked server-side during active rotation.
       */
      changePassword: async (oldPassword: string, newPassword: string) => {
        const resp = await fetch(`${apiUrl}/account/keypair-data`, { credentials: 'include' });
        if (!resp.ok) throw new Error('Failed to fetch keypair data');
        const keypairData = await resp.json();

        const body: Record<string, object> = {};
        for (const slotKey of ['slot_a', 'slot_b'] as const) {
          const slot = keypairData[slotKey];
          if (!slot) continue;
          // bypassCache=true: old password must be verified by actual decryption,
          // not silently accepted via a cached key from a previous session unlock.
          const oldKey = await crypto.deriveKeyFromPassword(oldPassword, slot.private_key_salt, true);
          let pem: string;
          try {
            pem = await crypto.decryptPrivateKeyToPem(oldKey, slot.encrypted_private_key, slot.private_key_nonce);
          } catch {
            throw new Error(translate.instant('account.changePwdWrongPassword'));
          }
          body[slotKey] = await crypto.encryptPrivateKeyForStorage(pem, newPassword);
        }

        const putResp = await fetch(`${apiUrl}/account/keypair/password`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!putResp.ok) { const e = await putResp.json(); throw new Error(e.detail || 'Password change failed'); }
      },

      /**
       * Decrypt the active slot's private key with the keypair password, build a
       * PKCS#12 file protected by exportPassword, and trigger a browser download.
       */
      exportP12: async (keypairPassword: string, exportPassword: string) => {
        const [statusResp, dataResp] = await Promise.all([
          fetch(`${apiUrl}/account/keypair`, { credentials: 'include' }),
          fetch(`${apiUrl}/account/keypair-data`, { credentials: 'include' }),
        ]);
        if (!statusResp.ok || !dataResp.ok) throw new Error('Failed to fetch keypair data');
        const keypairStatus = await statusResp.json();
        const keypairData   = await dataResp.json();
        const activeSlot: string = keypairStatus.key_active_slot || 'A';
        const slot = activeSlot === 'B' ? keypairData.slot_b : keypairData.slot_a;
        if (!slot) throw new Error('No keypair data found');

        const derivedKey    = await crypto.deriveKeyFromPassword(keypairPassword, slot.private_key_salt);
        const privateKeyPem = await crypto.decryptPrivateKeyToPem(derivedKey, slot.encrypted_private_key, slot.private_key_nonce);
        const p12Bytes      = await crypto.buildPkcs12(privateKeyPem, exportPassword);

        const blob = new Blob([p12Bytes], { type: 'application/x-pkcs12' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url; a.download = 'keypair.p12'; a.click();
        URL.revokeObjectURL(url);
      },
    };
  }

  showMyAccount(initialTab = 'txn'): void {
    this.showUserMenu = false;
    // Refresh balance before showing account popup
    this.auth.refreshProfile();
    const roles = (this.user?.roles || []).join(', ') || 'user';
    const balance = this.user?.balance !== null && this.user?.balance !== undefined
      ? this.user.balance.toLocaleString()
      : '—';

    const t = (key: string) => this.translate.instant(key);
    const apiUrl = this.apiUrl;
    const orgGroup = (this.user as any)?.org_group || '';
    const isSchemaAdmin = !!(this.user as any)?.schema_admin;
    const isDev = this.auth.isDeveloper;

    const popW = 900;
    const popH = 700;
    const left = Math.round((screen.width - popW) / 2);
    const top = Math.round((screen.height - popH) / 2);

    const currentTheme = (window as any).ddTheme?.get() || 'default';
    const isDarkNow = currentTheme === 'dark';

    const w = this.openPopup('my_account', `width=${popW},height=${popH},left=${left},top=${top},scrollbars=yes,resizable=yes`);
    if (!w) return;
    w.document.write(
      '<!DOCTYPE html><html><head><title>' + t('account.title') + '</title>' +
      '<style>' +
      'body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;padding:24px;background:#f9f9f9;color:#333;margin:0;}' +
      'h2{font-size:18px;margin:0 0 16px;color:#1a1a1a;}' +
      '.info-row{display:flex;align-items:center;padding:8px 0;border-bottom:1px solid #eee;}' +
      '.info-label{font-weight:600;width:80px;color:#666;}' +
      '.info-value{flex:1;}' +
      '.balance-value{font-size:20px;font-weight:600;color:#2e7d32;}' +
      '.coin-icon{margin-right:8px;}' +
      '.roles-badge{display:inline-block;background:#e3f2fd;color:#1565c0;padding:2px 8px;border-radius:12px;font-size:12px;margin-right:4px;}' +
      '.topup-section{margin-top:12px;padding-top:12px;border-top:2px solid #eee;}' +
      '.topup-section h3{font-size:14px;margin:0 0 8px;color:#1a1a1a;}' +
      '.tabs{display:flex;gap:0;margin-top:20px;border-bottom:2px solid #ddd;}' +
      '.tab{padding:8px 16px;cursor:pointer;border:none;background:none;font-size:13px;color:#666;border-bottom:2px solid transparent;margin-bottom:-2px;}' +
      '.tab.active{color:#1565c0;border-bottom-color:#1565c0;font-weight:600;}' +
      '.tab-content{display:none;margin-top:12px;}' +
      '.tab-content.active{display:block;}' +
      'table{width:100%;border-collapse:collapse;font-size:12px;}' +
      'th{text-align:left;padding:6px 8px;background:#f0f0f0;border-bottom:1px solid #ddd;font-weight:600;color:#555;white-space:nowrap;}' +
      'td{padding:5px 8px;border-bottom:1px solid #eee;white-space:nowrap;}' +
      'tr:hover td{background:#f5f8ff;}' +
      '.no-data{color:#999;text-align:center;padding:20px;font-style:italic;}' +
      '.loading{color:#999;text-align:center;padding:20px;}' +
      '.neg{color:#c62828;}.pos{color:#2e7d32;}' +
      '.sec-status{padding:12px;background:#f5f5f5;border-radius:6px;margin-bottom:16px;}' +
      '.sec-status .label{font-weight:600;color:#555;font-size:13px;}' +
      '.sec-status .value{font-size:13px;color:#333;margin-left:8px;}' +
      '.sec-status .active{color:#2e7d32;font-weight:600;}' +
      '.sec-status .inactive{color:#999;}' +
      '.sec-form{margin-top:12px;}' +
      '.sec-form label{display:block;font-size:13px;font-weight:600;color:#555;margin-bottom:4px;}' +
      '.sec-form input[type=password]{width:100%;padding:8px;border:1px solid #ccc;border-radius:4px;font-size:13px;box-sizing:border-box;}' +
      '.sec-form .hint{font-size:11px;color:#999;margin-top:2px;}' +
      '.btn{display:inline-block;padding:8px 16px;border:none;border-radius:4px;font-size:13px;cursor:pointer;margin-top:8px;margin-right:8px;}' +
      '.btn-primary{background:#1565c0;color:#fff;}.btn-primary:hover{background:#0d47a1;}' +
      '.btn-danger{background:#c62828;color:#fff;}.btn-danger:hover{background:#b71c1c;}' +
      '.btn-secondary{background:#546e7a;color:#fff;}.btn-secondary:hover{background:#37474f;}' +
      '.btn:disabled{opacity:0.5;cursor:not-allowed;}' +
      '.sec-msg{margin-top:8px;padding:8px;border-radius:4px;font-size:12px;}' +
      '.sec-msg.ok{background:#e8f5e9;color:#2e7d32;}' +
      '.sec-msg.err{background:#ffebee;color:#c62828;}' +
      '.sec-warning{margin-top:12px;padding:10px;background:#fff3e0;border-left:3px solid #ff9800;font-size:12px;color:#e65100;border-radius:2px;}' +
      '.sec-section{margin-top:16px;border-top:1px solid #eee;padding-top:12px;}' +
      '.sec-section h4{margin:0 0 8px;font-size:13px;color:#333;}' +
      '.progress-bar-wrap{background:#e0e0e0;border-radius:4px;height:8px;margin:8px 0;}' +
      '.progress-bar-fill{height:8px;border-radius:4px;background:#1565c0;transition:width .3s;}' +
      '.rotating-badge{display:inline-block;background:#fff3e0;color:#e65100;padding:2px 8px;border-radius:12px;font-size:12px;font-weight:600;}' +
      '.org-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;}' +
      '.org-group-name{font-size:13px;color:#555;}' +
      '.schema-table{width:100%;border-collapse:collapse;font-size:12px;margin-top:8px;}' +
      '.upload-form{margin-top:16px;padding:12px;background:#f5f5f5;border-radius:6px;}' +
      '.upload-form label{display:block;font-size:13px;font-weight:600;color:#555;margin-bottom:4px;}' +
      '.upload-form input[type=text],.upload-form input[type=file]{width:100%;padding:6px;border:1px solid #ccc;border-radius:4px;font-size:13px;box-sizing:border-box;margin-bottom:8px;}' +
      '.org-msg{margin-top:8px;padding:8px;border-radius:4px;font-size:12px;}' +
      '.org-msg.ok{background:#e8f5e9;color:#2e7d32;}.org-msg.err{background:#ffebee;color:#c62828;}' +
      '.theme-toggle-wrap{display:flex;align-items:center;gap:10px;}' +
      '.toggle-switch{position:relative;display:inline-block;width:44px;height:24px;flex-shrink:0;}' +
      '.toggle-switch input{opacity:0;width:0;height:0;position:absolute;}' +
      '.toggle-slider{position:absolute;cursor:pointer;inset:0;background:#ccc;border-radius:24px;transition:.25s;}' +
      '.toggle-slider:before{position:absolute;content:"";height:18px;width:18px;left:3px;bottom:3px;background:#fff;border-radius:50%;transition:.25s;}' +
      'input:checked+.toggle-slider{background:#3f51b5;}' +
      'input:checked+.toggle-slider:before{transform:translateX(20px);}' +
      'body.dark{background:#1e1e1e;color:#e0e0e0;}' +
      'body.dark h2{color:#e0e0e0;}' +
      'body.dark .info-row{border-bottom-color:#37474f;}' +
      'body.dark .info-label{color:#b0bec5;}' +
      'body.dark .roles-badge{background:#1a237e;color:#9fa8da;}' +
      'body.dark .topup-section{border-top-color:#37474f;}' +
      'body.dark .topup-section h3{color:#e0e0e0;}' +
      'body.dark .tabs{border-bottom-color:#37474f;}' +
      'body.dark .tab{color:#b0bec5;}' +
      'body.dark .tab.active{color:#9fa8da;border-bottom-color:#9fa8da;}' +
      'body.dark th{background:#2a2a2a;border-bottom-color:#37474f;color:#b0bec5;}' +
      'body.dark td{border-bottom-color:#263238;}' +
      'body.dark tr:hover td{background:#263238;}' +
      'body.dark .no-data,body.dark .loading{color:#78909c;}' +
      'body.dark .sec-status{background:#2a2a2a;}' +
      'body.dark .sec-status .label{color:#b0bec5;}' +
      'body.dark .sec-status .value{color:#e0e0e0;}' +
      'body.dark .sec-form label{color:#b0bec5;}' +
      'body.dark .sec-form input[type=password]{background:#252525;border-color:#37474f;color:#e0e0e0;}' +
      'body.dark .sec-warning{background:#3e2723;border-left-color:#ff9800;color:#ffb74d;}' +
      'body.dark .sec-section{border-top-color:#37474f;}' +
      'body.dark .sec-section h4{color:#e0e0e0;}' +
      'body.dark .sec-msg.ok{background:#1b5e20;color:#a5d6a7;}' +
      'body.dark .sec-msg.err{background:#b71c1c;color:#ef9a9a;}' +
      'body.dark .progress-bar-wrap{background:#37474f;}' +
      'body.dark .upload-form{background:#2a2a2a;}' +
      'body.dark .upload-form label{color:#b0bec5;}' +
      'body.dark .upload-form input{background:#252525;border-color:#37474f;color:#e0e0e0;}' +
      'body.dark .org-msg.ok{background:#1b5e20;color:#a5d6a7;}' +
      'body.dark .org-msg.err{background:#b71c1c;color:#ef9a9a;}' +
      'body.dark input:checked+.toggle-slider{background:#9fa8da;}' +
      '</style>' +
      '</head><body>' +
      '<h2>👤 ' + t('account.title') + '</h2>' +
      '<div class="info-row"><span class="info-label">' + t('account.name') + '</span><span class="info-value">' + (this.user?.name || '') + '</span></div>' +
      '<div class="info-row"><span class="info-label">' + t('account.email') + '</span><span class="info-value">' + (this.user?.email || '') + '</span></div>' +
      '<div class="info-row"><span class="info-label">' + t('account.roles') + '</span><span class="info-value">' +
      (this.user?.roles || ['user']).map((r: string) => '<span class="roles-badge">' + r + '</span>').join('') +
      '</span></div>' +
      '<div class="info-row"><span class="info-label">' + t('account.balance') + '</span><span class="info-value balance-value"><span class="coin-icon">ꝟ</span>' + balance + '</span></div>' +
      '<div class="info-row">' +
      '<span class="info-label">' + t('account.topup') + '</span>' +
      '<span class="info-value">' +
      '<form action="https://www.paypal.com/cgi-bin/webscr" method="post" target="_top" style="display:inline;margin:0;" onsubmit="if(typeof gtag===\'function\')gtag(\'event\',\'topup_initiated\')">' +
      '<input type="hidden" name="cmd" value="_s-xclick" />' +
      '<input type="hidden" name="hosted_button_id" value="' + this.paypalHostedButtonId + '" />' +
      '<input type="hidden" name="currency_code" value="EUR" />' +
      '<input type="hidden" name="custom" value="' + (this.user?.email || '') + '" />' +
      '<input type="image" src="https://www.paypalobjects.com/webstatic/en_US/i/btn/png/silver-rect-paypal-34px.png" style="border:0;cursor:pointer;vertical-align:middle;" name="submit" title="PayPal" alt="PayPal" />' +
      '</form>' +
      '</span>' +
      '</div>' +
      '<div class="info-row"><span class="info-label">' + t('account.theme') + '</span>' +
      '<span class="info-value"><span class="theme-toggle-wrap">' +
      '<label class="toggle-switch"><input type="checkbox" id="theme-chk"' + (isDarkNow ? ' checked' : '') + ' onchange="applyTheme(this.checked)" /><span class="toggle-slider"></span></label>' +
      '<span id="theme-lbl">' + (isDarkNow ? '☀️ ' + t('account.lightMode') : '🌙 ' + t('account.darkMode')) + '</span>' +
      '</span></span></div>' +
      '<div class="tabs">' +
      '<button class="tab' + (initialTab === 'txn' ? ' active' : '') + '" onclick="showTab(\'txn\')">' + t('account.transactions') + '</button>' +
      '<button class="tab' + (initialTab === 'evt' ? ' active' : '') + '" onclick="showTab(\'evt\')">' + t('account.billingEvents') + '</button>' +
      '<button class="tab' + (initialTab === 'sec' ? ' active' : '') + '" onclick="showTab(\'sec\')">🔐 ' + t('account.security') + '</button>' +
      (orgGroup ? '<button class="tab' + (initialTab === 'org' ? ' active' : '') + '" onclick="showTab(\'org\')">🏢 Organization</button>' : '') +
      (isDev ? '<button class="tab' + (initialTab === 'usr' ? ' active' : '') + '" onclick="showTab(\'usr\')">👥 Users</button>' : '') +
      (isDev ? '<button class="tab' + (initialTab === 'act' ? ' active' : '') + '" onclick="showTab(\'act\')">📊 Activity</button>' : '') +
      (isDev ? '<button class="tab' + (initialTab === 'svy' ? ' active' : '') + '" onclick="showTab(\'svy\')">📝 Surveys</button>' : '') +
      '</div>' +
      '<div id="txn" class="tab-content' + (initialTab === 'txn' ? ' active' : '') + '"><div class="loading">Loading...</div></div>' +
      '<div id="evt" class="tab-content' + (initialTab === 'evt' ? ' active' : '') + '"><div class="loading">Loading...</div></div>' +
      '<div id="sec" class="tab-content' + (initialTab === 'sec' ? ' active' : '') + '"><div class="loading">Loading...</div></div>' +
      (orgGroup ? '<div id="org" class="tab-content' + (initialTab === 'org' ? ' active' : '') + '"><div class="loading">Loading...</div></div>' : '') +
      (isDev ? '<div id="usr" class="tab-content' + (initialTab === 'usr' ? ' active' : '') + '"><div class="loading">Loading...</div></div>' : '') +
      (isDev ? '<div id="act" class="tab-content' + (initialTab === 'act' ? ' active' : '') + '"><div class="loading">Loading...</div></div>' : '') +
      (isDev ? '<div id="svy" class="tab-content' + (initialTab === 'svy' ? ' active' : '') + '"><div class="loading">Loading...</div></div>' : '') +
      '<script>' +
      'function applyTheme(dark){' +
      '  window.opener.ddTheme.set(dark?"dark":"default");' +
      '  document.body.classList.toggle("dark",dark);' +
      '  document.getElementById("theme-lbl").textContent=dark?"☀️ ' + t('account.lightMode') + '":"🌙 ' + t('account.darkMode') + '";' +
      '}' +
      'function showTab(id){' +
      '  document.querySelectorAll(".tab-content").forEach(function(el){el.classList.remove("active")});' +
      '  document.querySelectorAll(".tab").forEach(function(el){el.classList.remove("active")});' +
      '  document.getElementById(id).classList.add("active");' +
      '  event.target.classList.add("active");' +
      '}' +
      // Browser-locale date/time, matching the localDateTime pipe used in the
      // Angular views. Built once rather than per row.
      'var DT_FMT=new Intl.DateTimeFormat(undefined,{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});' +
      'function fmtDate(s){if(!s)return"-";var d=new Date(s);if(isNaN(d.getTime()))return"-";return DT_FMT.format(d);}' +
      'function loadTxn(){' +
      '  fetch("' + apiUrl + '/billing/transactions",{credentials:"include"})' +
      '  .then(function(r){return r.json()})' +
      '  .then(function(rows){' +
      '    if(!rows.length){document.getElementById("txn").innerHTML="<div class=\\"no-data\\">' + t('account.noData') + '</div>";return;}' +
      '    var h="<table><tr><th>' + t('account.date') + '</th><th>' + t('account.type') + '</th><th>' + t('account.amount') + '</th><th>' + t('account.balanceAfter') + '</th>' + (isDev ? '<th>' + t('account.description') + '</th>' : '') + '</tr>";' +
      '    rows.forEach(function(r){' +
      '      var cls=r.amount<0?"neg":"pos";' +
      '      h+="<tr><td>"+fmtDate(r.created_at)+"</td><td>"+r.transaction_type+"</td><td class=\\""+cls+"\\">"+r.amount.toFixed(2)+"</td><td>"+Number(r.balance_after).toFixed(2)+"</td>' + (isDev ? '<td>"+(r.description||"-")+"</td>' : '') + '</tr>";' +
      '    });' +
      '    h+="</table>";' +
      '    document.getElementById("txn").innerHTML=h;' +
      '  }).catch(function(){document.getElementById("txn").innerHTML="<div class=\\"no-data\\">Error loading data</div>";});' +
      '}' +
      'function loadEvt(){' +
      '  fetch("' + apiUrl + '/billing/events",{credentials:"include"})' +
      '  .then(function(r){return r.json()})' +
      '  .then(function(rows){' +
      '    if(!rows.length){document.getElementById("evt").innerHTML="<div class=\\"no-data\\">' + t('account.noData') + '</div>";return;}' +
      '    var h="<table><tr><th>' + t('account.date') + '</th><th>' + t('account.operation') + '</th><th>' + t('account.cost') + '</th>' + (isDev ? '<th>' + t('account.model') + '</th><th>' + t('account.inputTokens') + '</th><th>' + t('account.outputTokens') + '</th>' : '') + '<th>' + t('account.ocrWords') + '</th></tr>";' +
      '    rows.forEach(function(r){' +
      '      h+="<tr><td>"+fmtDate(r.created_at)+"</td><td>"+r.operation+"</td><td>"+Number(r.cost).toFixed(2)+"</td>' + (isDev ? '<td>"+(r.llm_model||r.ocr_backend||"-")+"</td><td>"+(r.llm_input_tokens||"-")+"</td><td>"+(r.llm_output_tokens||"-")+"</td>' : '') + '<td>"+(r.ocr_words||"-")+"</td></tr>";' +
      '    });' +
      '    h+="</table>";' +
      '    document.getElementById("evt").innerHTML=h;' +
      '  }).catch(function(){document.getElementById("evt").innerHTML="<div class=\\"no-data\\">Error loading data</div>";});' +
      '}' +
      'function loadSec(){' +
      '  fetch("' + apiUrl + '/account/keypair",{credentials:"include"})' +
      '  .then(function(r){return r.json()})' +
      '  .then(function(data){' +
      '    var el=document.getElementById("sec");' +
      '    var h="<div class=\\"sec-status\\">";' +
      '    h+="<span class=\\"label\\">' + t('account.keypairStatus') + ':</span> ";' +
      '    if(data.has_keypair){' +
      '      h+="<span class=\\"value active\\">✓ ' + t('account.keypairActive') + '</span><br/>";' +
      '      h+="<span class=\\"label\\">' + t('account.keypairType') + ':</span> ";' +
      '      h+="<span class=\\"value\\">"+(data.key_type==="server"?"' + t('account.keypairServer') + '":"' + t('account.keypairClient') + '")+"</span><br/>";' +
      '      h+="<span class=\\"label\\">' + t('account.keypairSlot') + ':</span> <span class=\\"value\\">Slot "+(data.key_active_slot||"A")+"</span><br/>";' +
      '      if(data.rotation_status==="rotating"){h+="<span class=\\"label\\">' + t('account.rotationStatus') + ':</span> <span class=\\"rotating-badge\\">' + t('account.rotationInProgress') + '</span><br/>";}' +
      '      if(data.keypair_created_at){h+="<span class=\\"label\\">' + t('account.keypairCreated') + ':</span> <span class=\\"value\\">"+fmtDate(data.keypair_created_at)+"</span>";}' +
      '      h+="</div>";' +
      '      if(data.rotation_status==="rotating"){' +
      '        h+="<div class=\\"sec-section\\"><h4>' + t('account.rotationInProgress') + '</h4>";' +
      '        h+="<div class=\\"sec-form\\">";' +
      '        h+="<label>' + t('account.keypairPasswordLabel') + '</label>";' +
      '        h+="<input type=\\"password\\" id=\\"rot-pass\\" />";' +
      '        h+="<button class=\\"btn btn-primary\\" onclick=\\"continueRotation()\\">' + t('account.rotateKeyContinue') + '</button>";' +
      '        h+="</div></div>";' +
      '      }else{' +
      '        h+="<div class=\\"sec-section\\"><h4>' + t('account.rotateKey') + '</h4>";' +
      '        h+="<div class=\\"sec-form\\">";' +
      '        h+="<label>' + t('account.keypairPasswordLabel') + '</label>";' +
      '        h+="<input type=\\"password\\" id=\\"rot-pass\\" />";' +
      '        h+="<button class=\\"btn btn-secondary\\" onclick=\\"rotateKey()\\">' + t('account.rotateKey') + '</button>";' +
      '        h+="</div></div>";' +
      '      }' +
      '      h+="<div class=\\"sec-section\\"><h4>' + t('account.exportP12') + '</h4>";' +
      '      h+="<div class=\\"sec-form\\">";' +
      '      h+="<label>' + t('account.keypairPasswordLabel') + '</label>";' +
      '      h+="<input type=\\"password\\" id=\\"p12-kp-pass\\" />";' +
      '      h+="<label style=\\"margin-top:6px\\">' + t('account.exportP12ExportPassword') + '</label>";' +
      '      h+="<input type=\\"password\\" id=\\"p12-exp-pass\\" />";' +
      '      h+="<button class=\\"btn btn-secondary\\" onclick=\\"exportP12()\\">' + t('account.exportP12Submit') + '</button>";' +
      '      h+="</div></div>";' +
      '      h+="<div class=\\"sec-section\\"><h4>' + t('account.changePwd') + '</h4>";' +
      '      h+="<div class=\\"sec-form\\">";' +
      '      h+="<label>' + t('account.changePwdOld') + '</label>";' +
      '      h+="<input type=\\"password\\" id=\\"cpwd-old\\" />";' +
      '      h+="<label style=\\"margin-top:6px\\">' + t('account.changePwdNew') + '</label>";' +
      '      h+="<input type=\\"password\\" id=\\"cpwd-new\\" />";' +
      '      h+="<label style=\\"margin-top:6px\\">' + t('account.keypairConfirmLabel') + '</label>";' +
      '      h+="<input type=\\"password\\" id=\\"cpwd-confirm\\" />";' +
      '      h+="<button class=\\"btn btn-secondary\\" onclick=\\"changePwd()\\">' + t('account.changePwdSubmit') + '</button>";' +
      '      h+="</div></div>";' +
      '      h+="<div class=\\"sec-section\\">";' +
      '      h+="<div class=\\"sec-warning\\">' + t('account.keypairDeleteWarning') + '</div>";' +
      '      h+="<button class=\\"btn btn-danger\\" onclick=\\"deleteKeypair()\\">' + t('account.keypairDelete') + '</button>";' +
      '      h+="</div>";' +
      '    }else{' +
      '      h+="<span class=\\"value inactive\\">' + t('account.keypairNone') + '</span>";' +
      '      h+="</div>";' +
      '      h+="<div class=\\"sec-form\\">";' +
      '      h+="<label>' + t('account.keypairPasswordLabel') + '</label>";' +
      '      h+="<input type=\\"password\\" id=\\"kp-pass\\" placeholder=\\"' + t('account.keypairPasswordPlaceholder') + '\\" />";' +
      '      h+="<div class=\\"hint\\">' + t('account.keypairPasswordHint') + '</div>";' +
      '      h+="<label style=\\"margin-top:8px\\">' + t('account.keypairConfirmLabel') + '</label>";' +
      '      h+="<input type=\\"password\\" id=\\"kp-pass2\\" placeholder=\\"' + t('account.keypairConfirmPlaceholder') + '\\" />";' +
      '      h+="<button class=\\"btn btn-primary\\" onclick=\\"generateKeypair()\\">' + t('account.keypairGenerate') + '</button>";' +
      '      h+="</div>";' +
      '    }' +
      '    h+="<div id=\\"sec-msg\\"></div>";' +
      '    el.innerHTML=h;' +
      '  }).catch(function(){document.getElementById("sec").innerHTML="<div class=\\"no-data\\">Error loading security data</div>";});' +
      '}' +
      'function generateKeypair(){' +
      '  var p1=document.getElementById("kp-pass").value;' +
      '  var p2=document.getElementById("kp-pass2").value;' +
      '  var msg=document.getElementById("sec-msg");' +
      '  if(!p1||p1.length<8){msg.className="sec-msg err";msg.textContent="' + t('account.keypairPasswordTooShort') + '";return;}' +
      '  if(p1!==p2){msg.className="sec-msg err";msg.textContent="' + t('account.keypairPasswordMismatch') + '";return;}' +
      '  msg.className="sec-msg";msg.textContent="' + t('account.keypairGenerating') + '";' +
      '  window.opener.ddKeyMgmt.createKeypair(p1)' +
      '  .then(function(){msg.className="sec-msg ok";msg.textContent="' + t('account.keypairCreated') + '";setTimeout(loadSec,1500);})' +
      '  .catch(function(e){msg.className="sec-msg err";msg.textContent=e.message;});' +
      '}' +
      'function rotateKey(){' +
      '  var pass=document.getElementById("rot-pass").value;' +
      '  var msg=document.getElementById("sec-msg");' +
      '  if(!pass){msg.className="sec-msg err";msg.textContent="' + t('account.keypairPasswordLabel') + '";return;}' +
      '  msg.className="sec-msg";msg.textContent="' + t('account.rotateKeyRunning') + '";' +
      '  window.opener.ddKeyMgmt.generateAndRotate(pass)' +
      '  .then(function(ctx){' +
      '    return runRewrapLoop(ctx,pass);' +
      '  })' +
      '  .then(function(){msg.className="sec-msg ok";msg.textContent="' + t('account.rotateKeyDone') + '";setTimeout(loadSec,1500);})' +
      '  .catch(function(e){msg.className="sec-msg err";msg.textContent=e.message;});' +
      '}' +
      'function continueRotation(){' +
      '  var pass=document.getElementById("rot-pass").value;' +
      '  var msg=document.getElementById("sec-msg");' +
      '  if(!pass){msg.className="sec-msg err";msg.textContent="' + t('account.keypairPasswordLabel') + '";return;}' +
      '  msg.className="sec-msg";msg.textContent="' + t('account.rotateKeyRunning') + '";' +
      '  fetch("' + apiUrl + '/account/keypair/rotation-status",{credentials:"include"})' +
      '  .then(function(r){return r.json()})' +
      '  .then(function(status){' +
      '    return fetch("' + apiUrl + '/account/keypair-data",{credentials:"include"})' +
      '    .then(function(r){return r.json()})' +
      '    .then(function(kd){' +
      '      var rSlot=status.retiring_slot;' +
      '      var rData=rSlot==="B"?kd.slot_b:kd.slot_a;' +
      '      return fetch("' + apiUrl + '/account/keypair",{credentials:"include"})' +
      '      .then(function(r){return r.json()})' +
      '      .then(function(ks){' +
      '        var newSlot=ks.key_active_slot;' +
      '        var newSlotData=newSlot==="B"?kd.slot_b:kd.slot_a;' +
      '        var newPub=newSlotData?newSlotData.public_key_pem:null;' +
      '        return runRewrapLoop({retiringSlot:rSlot,retiringSlotData:rData,newPublicKeyPem:newPub},pass);' +
      '      });' +
      '    });' +
      '  })' +
      '  .then(function(){msg.className="sec-msg ok";msg.textContent="' + t('account.rotateKeyDone') + '";setTimeout(loadSec,1500);})' +
      '  .catch(function(e){msg.className="sec-msg err";msg.textContent=e.message;});' +
      '}' +
      'function runRewrapLoop(ctx,pass){' +
      '  return fetch("' + apiUrl + '/dossier",{credentials:"include"})' +
      '  .then(function(r){if(!r.ok)return r.json().then(function(e){throw new Error("Dossier list failed (HTTP "+r.status+"): "+(e.detail||JSON.stringify(e)));});return r.json();})' +
      '  .then(function(dossiers){' +
      '    if(!Array.isArray(dossiers))throw new Error("Expected array from /dossier, got "+typeof dossiers+": "+JSON.stringify(dossiers).substring(0,120));' +
      '    var list=dossiers.filter(function(d){return d.key_slot===ctx.retiringSlot&&d.encrypted_dek;});' +
      '    var total=list.length;' +
      '    var done=0;' +
      '    var msg=document.getElementById("sec-msg");' +
      '    if(total===0)return Promise.resolve();' +
      '    function next(){' +
      '      if(done>=total)return Promise.resolve();' +
      '      var d=list[done];' +
      '      msg.className="sec-msg";' +
      '      msg.textContent="' + t('account.rotateKeyProgress') + ' "+(done+1)+" / "+total+"…";' +
      '      done++;' +
      '      return window.opener.ddKeyMgmt.rewrapDossier(d.oid,d.encrypted_dek,pass,ctx.retiringSlotData,ctx.newPublicKeyPem).then(next);' +
      '    }' +
      '    return next();' +
      '  });' +
      '}' +
      'function exportP12(){' +
      '  var kpPass=document.getElementById("p12-kp-pass").value;' +
      '  var expPass=document.getElementById("p12-exp-pass").value;' +
      '  var msg=document.getElementById("sec-msg");' +
      '  if(!kpPass||!expPass){msg.className="sec-msg err";msg.textContent="' + t('account.exportP12ExportPassword') + '";return;}' +
      '  msg.className="sec-msg";msg.textContent="' + t('account.exportP12Running') + '";' +
      '  window.opener.ddKeyMgmt.exportP12(kpPass,expPass)' +
      '  .then(function(){msg.className="sec-msg ok";msg.textContent="' + t('account.exportP12Done') + '";})' +
      '  .catch(function(e){msg.className="sec-msg err";msg.textContent=e.message;});' +
      '}' +
      'function changePwd(){' +
      '  var oldP=document.getElementById("cpwd-old").value;' +
      '  var newP=document.getElementById("cpwd-new").value;' +
      '  var cfm=document.getElementById("cpwd-confirm").value;' +
      '  var msg=document.getElementById("sec-msg");' +
      '  if(!oldP||!newP){msg.className="sec-msg err";msg.textContent="' + t('account.keypairPasswordLabel') + '";return;}' +
      '  if(newP.length<8){msg.className="sec-msg err";msg.textContent="' + t('account.keypairPasswordTooShort') + '";return;}' +
      '  if(newP!==cfm){msg.className="sec-msg err";msg.textContent="' + t('account.keypairPasswordMismatch') + '";return;}' +
      '  msg.className="sec-msg";msg.textContent="' + t('account.changePwdRunning') + '";' +
      '  window.opener.ddKeyMgmt.changePassword(oldP,newP)' +
      '  .then(function(){msg.className="sec-msg ok";msg.textContent="' + t('account.changePwdDone') + '";})' +
      '  .catch(function(e){msg.className="sec-msg err";msg.textContent=e.message;});' +
      '}' +
      'function deleteKeypair(){' +
      '  if(!confirm("' + t('account.keypairDeleteConfirm') + '"))return;' +
      '  var msg=document.getElementById("sec-msg");' +
      '  fetch("' + apiUrl + '/account/keypair",{method:"DELETE",credentials:"include"})' +
      '  .then(function(r){if(!r.ok)throw new Error("Error");return r.json()})' +
      '  .then(function(){msg.className="sec-msg ok";msg.textContent="' + t('account.keypairDeleted') + '";setTimeout(loadSec,1500);})' +
      '  .catch(function(e){msg.className="sec-msg err";msg.textContent=e.message;});' +
      '}' +
      (orgGroup ?
      'function loadOrg(){' +
      '  var el=document.getElementById("org");' +
      '  var isAdmin=' + (isSchemaAdmin ? 'true' : 'false') + ';' +
      '  Promise.all([' +
      '    fetch("' + apiUrl + '/schemas",{credentials:"include"}).then(function(r){if(!r.ok)throw new Error("HTTP "+r.status);return r.json()}),' +
      '    fetch("' + apiUrl + '/dossier/my-org/downstream-endpoint",{credentials:"include"}).then(function(r){if(!r.ok)throw new Error("HTTP "+r.status);return r.json()}),' +
      '    fetch("' + apiUrl + '/dossier/my-org/branding",{credentials:"include"}).then(function(r){return r.ok?r.json():{};}).catch(function(){return {};})' +
      '  ]).then(function(res){' +
      '    var schemas=res[0];var ep=res[1];var brand=res[2]||{};' +
      '    var h="<div class=\\"org-header\\">";' +
      '    h+="<span class=\\"org-group-name\\">🏢 <strong>' + orgGroup + '</strong></span>";' +
      '    h+="</div>";' +
      '    if(!schemas.length){h+="<div class=\\"no-data\\">No schemas uploaded yet.</div>";}' +
      '    else{' +
      '      h+="<table class=\\"schema-table\\"><tr><th>Name</th><th>Last modified</th>" + (isAdmin?"<th></th>":"") + "</tr>";' +
      '      schemas.forEach(function(s){' +
      '        h+="<tr><td>"+s.name+"</td><td>"+(s.last_modified?fmtDate(s.last_modified):"-")+"</td>";' +
      '        if(isAdmin){h+="<td><button class=\\"btn btn-danger\\" style=\\"margin:0;padding:3px 10px;\\" onclick=\\"deleteSchema(\'"+s.name+"\')\\" >Delete</button></td>";}' +
      '        h+="</tr>";' +
      '      });' +
      '      h+="</table>";' +
      '    }' +
      '    if(isAdmin){' +
      '      h+="<div class=\\"upload-form\\">";' +
      '      h+="<h4 style=\\"margin:0 0 10px;font-size:13px;\\">Upload XSD schemas</h4>";' +
      '      h+="<label>XSD files (names taken from filenames)</label>";' +
      '      h+="<input type=\\"file\\" id=\\"sch-file\\" accept=\\".xsd,text/xml,application/xml\\" multiple />";' +
      '      h+="<button class=\\"btn btn-primary\\" onclick=\\"uploadSchema()\\">Upload</button>";' +
      '      h+="</div>";' +
      '    }' +
      '    h+="<div class=\\"upload-form\\" style=\\"margin-top:18px;\\">";' +
      '    h+="<h4 style=\\"margin:0 0 10px;font-size:13px;\\">Dossier Submission Endpoint</h4>";' +
      '    if(isAdmin){' +
      '      h+="<label>Endpoint URL</label>";' +
      '      h+="<input type=\\"url\\" id=\\"ep-url\\" style=\\"width:100%;box-sizing:border-box;\\" placeholder=\\"https://...\\" value=\\""+(ep.endpoint_url||"")+"\\" />";' +
      '      h+="<button class=\\"btn btn-primary\\" style=\\"margin-top:6px;\\" onclick=\\"saveEndpoint()\\">Save</button>";' +
      '    } else {' +
      '      h+="<div style=\\"font-size:13px;color:#555;\\">"+(ep.endpoint_url||"<em>Not configured</em>")+"</div>";' +
      '    }' +
      'h+="</div>";' +
      'h+="<div class=\\"upload-form\\" style=\\"margin-top:18px;\\">";' +
      'h+="<h4 style=\\"margin:0 0 10px;font-size:13px;\\">Branding</h4>";' +
      'h+="<div style=\\"margin-bottom:10px;\\">"+(brand.logo_url?"<img src=\\""+brand.logo_url+"\\" alt=\\"\\" style=\\"height:40px;width:auto;display:block;background:#fff;border-radius:8px;padding:3px 8px;\\" />":"<span style=\\"font-size:12px;color:#888;\\">Using the default Irisel logo</span>")+"</div>";' +
      'if(isAdmin){' +
      '  h+="<label>Logo — PNG, JPEG or WebP, max 512 KB</label>";' +
      '  h+="<input type=\\"file\\" id=\\"brand-logo-file\\" accept=\\"image/png,image/jpeg,image/webp\\" />";' +
      '  h+="<button class=\\"btn btn-primary\\" onclick=\\"uploadBrandingLogo()\\">Upload logo</button>";' +
      '  if(brand.logo_url){h+=" <button class=\\"btn btn-danger\\" onclick=\\"resetBranding()\\">Reset to Irisel branding</button>";}' +
      '  h+="<div style=\\"margin-top:14px;\\">";' +
      '  h+="<label>Product name</label><input type=\\"text\\" id=\\"brand-product\\" placeholder=\\"' + t('app.title') + '\\" value=\\""+(brand.product_name||"")+"\\" />";' +
      '  h+="<label>Legal name — shown as the copyright holder in About</label><input type=\\"text\\" id=\\"brand-legal\\" placeholder=\\"Irisel Consulting SLU\\" value=\\""+(brand.legal_name||"")+"\\" />";' +
      '  h+="<label>Terms URL — shown as the About terms link</label><input type=\\"url\\" id=\\"brand-terms\\" placeholder=\\"https://...\\" value=\\""+(brand.terms_url||"")+"\\" />";' +
      '  h+="<label>Site URL</label><input type=\\"url\\" id=\\"brand-site\\" placeholder=\\"https://...\\" value=\\""+(brand.site_url||"")+"\\" />";' +
      '  h+="<label>Logo link — where the header logo points, blank for unlinked</label><input type=\\"url\\" id=\\"brand-link\\" placeholder=\\"https://...\\" value=\\""+(brand.link_url||"")+"\\" />";' +
      '  h+="<button class=\\"btn btn-primary\\" style=\\"margin-top:6px;\\" onclick=\\"saveBrandingText()\\">Save branding text</button>";' +
      '  h+="</div>";' +
      '}' +
      'h+="</div>";' +
      'h+="<div id=\\"org-msg\\"></div>";' +
      'el.innerHTML=h;' +
      '  }).catch(function(e){el.innerHTML="<div class=\\"no-data\\">Error loading organization data: "+e.message+"</div>";});' +
      '}' +
      'function deleteSchema(name){' +
      '  if(!confirm("Delete schema \'"+name+"\'? This cannot be undone."))return;' +
      '  var msg=document.getElementById("org-msg");' +
      '  fetch("' + apiUrl + '/schemas/"+name,{method:"DELETE",credentials:"include"})' +
      '  .then(function(r){if(!r.ok)return r.json().then(function(e){throw new Error(e.detail||"Error");});})' +
      '  .then(function(){if(msg){msg.className="org-msg ok";msg.textContent="Schema deleted.";}setTimeout(loadOrg,800);})' +
      '  .catch(function(e){if(msg){msg.className="org-msg err";msg.textContent=e.message;}});' +
      '}' +
      'function uploadSchema(){' +
      '  var fileInput=document.getElementById("sch-file");' +
      '  var msg=document.getElementById("org-msg");' +
      '  if(!fileInput.files||!fileInput.files.length){msg.className="org-msg err";msg.textContent="Please select at least one XSD file.";return;}' +
      '  var fd=new FormData();' +
      '  for(var i=0;i<fileInput.files.length;i++){fd.append("files",fileInput.files[i]);}' +
      '  msg.className="org-msg";msg.textContent="Uploading "+fileInput.files.length+" file(s)...";' +
      '  fetch("' + apiUrl + '/schemas",{method:"POST",credentials:"include",body:fd})' +
      '  .then(function(r){return r.json().then(function(data){if(!r.ok)throw new Error(data.detail||"Upload failed");return data;})})' +
      '  .then(function(data){msg.className="org-msg ok";msg.textContent="Uploaded: "+data.map(function(s){return s.name;}).join(", ")+".";setTimeout(loadOrg,800);})' +
      '  .catch(function(e){msg.className="org-msg err";msg.textContent=e.message;});' +
      '}' +
      'function saveEndpoint(){' +
      '  var url=(document.getElementById("ep-url")||{}).value||"";' +
      '  var msg=document.getElementById("org-msg");' +
      '  if(!url){msg.className="org-msg err";msg.textContent="Please enter a URL.";return;}' +
      '  msg.className="org-msg";msg.textContent="Saving...";' +
      '  fetch("' + apiUrl + '/dossier/my-org/downstream-endpoint",{method:"PUT",credentials:"include",headers:{"Content-Type":"application/json"},body:JSON.stringify({endpoint_url:url})})' +
      '  .then(function(r){return r.json().then(function(data){if(!r.ok)throw new Error(data.detail||"Error");return data;})})' +
      '  .then(function(){msg.className="org-msg ok";msg.textContent="Endpoint saved.";})' +
      '  .catch(function(e){msg.className="org-msg err";msg.textContent=e.message;});' +
      '}' +
      'function uploadBrandingLogo(){' +
      '  var fileInput=document.getElementById("brand-logo-file");' +
      '  var msg=document.getElementById("org-msg");' +
      '  if(!fileInput.files||!fileInput.files.length){msg.className="org-msg err";msg.textContent="Please select a logo file.";return;}' +
      '  var fd=new FormData();' +
      '  fd.append("file",fileInput.files[0]);' +
      '  msg.className="org-msg";msg.textContent="Uploading logo...";' +
      '  fetch("' + apiUrl + '/dossier/my-org/branding/logo",{method:"PUT",credentials:"include",body:fd})' +
      '  .then(function(r){return r.json().then(function(data){if(!r.ok)throw new Error(data.detail||"Upload failed");return data;})})' +
      '  .then(function(){msg.className="org-msg ok";msg.textContent="Logo updated.";if(window.opener&&window.opener.ddBranding)window.opener.ddBranding.refresh();setTimeout(loadOrg,800);})' +
      '  .catch(function(e){msg.className="org-msg err";msg.textContent=e.message;});' +
      '}' +
      'function saveBrandingText(){' +
      '  var msg=document.getElementById("org-msg");' +
      '  var body={' +
      '    product_name:(document.getElementById("brand-product")||{}).value||null,' +
      '    legal_name:(document.getElementById("brand-legal")||{}).value||null,' +
      '    terms_url:(document.getElementById("brand-terms")||{}).value||null,' +
      '    site_url:(document.getElementById("brand-site")||{}).value||null,' +
      '    link_url:(document.getElementById("brand-link")||{}).value||null' +
      '  };' +
      '  msg.className="org-msg";msg.textContent="Saving...";' +
      '  fetch("' + apiUrl + '/dossier/my-org/branding",{method:"PUT",credentials:"include",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)})' +
      '  .then(function(r){return r.json().then(function(data){if(!r.ok)throw new Error(data.detail||"Error");return data;})})' +
      '  .then(function(){msg.className="org-msg ok";msg.textContent="Branding saved.";if(window.opener&&window.opener.ddBranding)window.opener.ddBranding.refresh();setTimeout(loadOrg,800);})' +
      '  .catch(function(e){msg.className="org-msg err";msg.textContent=e.message;});' +
      '}' +
      'function resetBranding(){' +
      '  if(!confirm("Reset to the default Irisel branding? This removes the uploaded logo and text."))return;' +
      '  var msg=document.getElementById("org-msg");' +
      '  fetch("' + apiUrl + '/dossier/my-org/branding",{method:"DELETE",credentials:"include"})' +
      '  .then(function(r){if(!r.ok)return r.json().then(function(e){throw new Error(e.detail||"Error");});})' +
      '  .then(function(){msg.className="org-msg ok";msg.textContent="Branding reset.";if(window.opener&&window.opener.ddBranding)window.opener.ddBranding.refresh();setTimeout(loadOrg,800);})' +
      '  .catch(function(e){msg.className="org-msg err";msg.textContent=e.message;});' +
      '}' : '') +
      (isDev ?
      'var ddSortState={};' +
      'var ddTableRenderers={};' +
      'function ddSort(tableId,col,type){' +
      '  var s=ddSortState[tableId];' +
      '  if(s&&s.col===col){s.dir=s.dir==="asc"?"desc":"asc";}' +
      '  else{ddSortState[tableId]={col:col,dir:"asc",type:type};}' +
      '  if(ddTableRenderers[tableId])ddTableRenderers[tableId]();' +
      '}' +
      'function ddSortRows(rows,col,dir,type){' +
      '  return rows.slice().sort(function(a,b){' +
      '    var av=a[col],bv=b[col];' +
      '    if(av==null&&bv==null)return 0;' +
      '    if(av==null)return 1;' +
      '    if(bv==null)return -1;' +
      '    if(type==="num"){av=Number(av);bv=Number(bv);}' +
      '    else if(type==="str"){av=String(av).toLowerCase();bv=String(bv).toLowerCase();}' +
      '    if(av<bv)return dir==="asc"?-1:1;' +
      '    if(av>bv)return dir==="asc"?1:-1;' +
      '    return 0;' +
      '  });' +
      '}' +
      'function ddSortInd(tableId,col){' +
      '  var s=ddSortState[tableId];' +
      '  return (s&&s.col===col)?(s.dir==="asc"?" ▲":" ▼"):"";' +
      '}' +
      'function loadUsr(){' +
      '  var el=document.getElementById("usr");' +
      '  Promise.all([' +
      '    fetch("' + apiUrl + '/admin/views/users_analysis",{credentials:"include"}).then(function(r){if(!r.ok)throw new Error("HTTP "+r.status);return r.json()}),' +
      '    fetch("' + apiUrl + '/admin/users",{credentials:"include"}).then(function(r){if(!r.ok)throw new Error("HTTP "+r.status);return r.json()})' +
      '  ]).then(function(res){' +
      '    var lifecycle=res[0],users=res[1];' +
      '    el.innerHTML="<h4 style=\\"margin:0 0 8px;font-size:13px;\\">User Lifecycle</h4><div id=\\"usr-lc\\"></div>"' +
      '      +"<h4 style=\\"margin:18px 0 8px;font-size:13px;\\">User List</h4><div id=\\"usr-lst\\"></div>"' +
      '      +"<div id=\\"usr-msg\\"></div>";' +
      '    ddTableRenderers["usr-lc"]=function(){' +
      '      if(!ddSortState["usr-lc"])ddSortState["usr-lc"]={col:"last_seen_days",dir:"desc",type:"num"};' +
      '      var s=ddSortState["usr-lc"];' +
      '      var rows=ddSortRows(lifecycle,s.col,s.dir,s.type);' +
      '      if(!rows.length){document.getElementById("usr-lc").innerHTML="<div class=\\"no-data\\">No users.</div>";return;}' +
      '      var h="<table><tr>"' +
      '        +"<th onclick=\\"ddSort(\'usr-lc\',\'username\',\'str\')\\" style=\\"cursor:pointer\\">Username"+ddSortInd("usr-lc","username")+"</th>"' +
      '        +"<th onclick=\\"ddSort(\'usr-lc\',\'active_days\',\'num\')\\" style=\\"cursor:pointer;text-align:right\\">Active days"+ddSortInd("usr-lc","active_days")+"</th>"' +
      '        +"<th onclick=\\"ddSort(\'usr-lc\',\'age_days\',\'num\')\\" style=\\"cursor:pointer;text-align:right\\">Age (days)"+ddSortInd("usr-lc","age_days")+"</th>"' +
      '        +"<th onclick=\\"ddSort(\'usr-lc\',\'last_seen_days\',\'num\')\\" style=\\"cursor:pointer;text-align:right\\">Last seen (days ago)"+ddSortInd("usr-lc","last_seen_days")+"</th>"' +
      '        +"</tr>";' +
      '      rows.forEach(function(r){' +
      '        h+="<tr><td>"+(r.username||"-")+"</td>"' +
      '          +"<td style=\\"text-align:right\\">"+(r.active_days==null?"-":r.active_days)+"</td>"' +
      '          +"<td style=\\"text-align:right\\">"+(r.age_days==null?"-":r.age_days)+"</td>"' +
      '          +"<td style=\\"text-align:right\\">"+(r.last_seen_days==null?"-":r.last_seen_days)+"</td></tr>";' +
      '      });' +
      '      h+="</table>";' +
      '      document.getElementById("usr-lc").innerHTML=h;' +
      '    };' +
      '    ddTableRenderers["usr-lst"]=function(){' +
      '      if(!ddSortState["usr-lst"])ddSortState["usr-lst"]={col:"email",dir:"asc",type:"str"};' +
      '      var s=ddSortState["usr-lst"];' +
      '      var rows=ddSortRows(users,s.col,s.dir,s.type);' +
      '      if(!rows.length){document.getElementById("usr-lst").innerHTML="<div class=\\"no-data\\">No users found.</div>";return;}' +
      '      var h="<table><tr>"' +
      '        +"<th onclick=\\"ddSort(\'usr-lst\',\'email\',\'str\')\\" style=\\"cursor:pointer\\">Email"+ddSortInd("usr-lst","email")+"</th>"' +
      '        +"<th onclick=\\"ddSort(\'usr-lst\',\'username\',\'str\')\\" style=\\"cursor:pointer\\">Username"+ddSortInd("usr-lst","username")+"</th>"' +
      '        +"<th onclick=\\"ddSort(\'usr-lst\',\'tradefinance\',\'num\')\\" style=\\"cursor:pointer;text-align:center\\">Trade Finance"+ddSortInd("usr-lst","tradefinance")+"</th>"' +
      '        +"</tr>";' +
      '      rows.forEach(function(u){' +
      '        var chk="<input type=\\"checkbox\\" onchange=\\"setTradefinance(\'"+u.keycloak_id+"\',this.checked)\\""+(u.tradefinance?" checked":"")+">";' +
      '        h+="<tr><td>"+u.email+"</td><td>"+u.username+"</td><td style=\\"text-align:center\\">"+chk+"</td></tr>";' +
      '      });' +
      '      h+="</table>";' +
      '      document.getElementById("usr-lst").innerHTML=h;' +
      '    };' +
      '    ddTableRenderers["usr-lc"]();' +
      '    ddTableRenderers["usr-lst"]();' +
      '  }).catch(function(e){el.innerHTML="<div class=\\"no-data\\">Error: "+e.message+"</div>";});' +
      '}' +
      'function setTradefinance(keycloakId,enabled){' +
      '  var msg=document.getElementById("usr-msg");' +
      '  if(msg){msg.className="org-msg";msg.textContent="Saving...";}' +
      '  fetch("' + apiUrl + '/admin/users/"+keycloakId+"/tradefinance",{method:"PATCH",credentials:"include",headers:{"Content-Type":"application/json"},body:JSON.stringify({enabled:enabled})})' +
      '  .then(function(r){if(!r.ok)return r.json().then(function(e){throw new Error(e.detail||"Error");});return r.json();})' +
      '  .then(function(){if(msg){msg.className="org-msg ok";msg.textContent="Saved.";setTimeout(function(){if(msg)msg.textContent="";},2000);}})' +
      '  .catch(function(e){if(msg){msg.className="org-msg err";msg.textContent=e.message;}});' +
      '}' +
      'function loadAct(){' +
      '  var el=document.getElementById("act");' +
      '  fetch("' + apiUrl + '/admin/views/kpi_activity_daily",{credentials:"include"})' +
      '  .then(function(r){if(!r.ok)throw new Error("HTTP "+r.status);return r.json()})' +
      '  .then(function(rows){' +
      '    if(!rows.length){el.innerHTML="<div class=\\"no-data\\">No activity data.</div>";return;}' +
      '    rows.sort(function(a,b){return a.activity_date<b.activity_date?-1:1;});' +
      '    var recent=rows.slice(-90);' +
      '    var metrics=[{key:"registrations",label:"Registrations"},{key:"logins",label:"Logins"},{key:"new_jobs",label:"New jobs"},{key:"new_dossiers",label:"New dossiers"},{key:"extractions",label:"Extractions"},{key:"dossier_extractions",label:"Dossier extractions"},{key:"total_extractions",label:"Total extractions"}];' +
      '    function sumLast(arr,k,n){var s=0;for(var i=Math.max(0,arr.length-n);i<arr.length;i++)s+=Number(arr[i][k])||0;return s;}' +
      '    function spark(arr,k){' +
      '      var vals=arr.map(function(r){return Number(r[k])||0;});' +
      '      var max=Math.max.apply(null,vals.concat([1]));' +
      '      var w=140,h=22,n=vals.length;' +
      '      if(n<2)return "";' +
      '      var pts=vals.map(function(v,i){var x=(i/(n-1))*w;var y=h-(v/max)*h;return x.toFixed(1)+","+y.toFixed(1);}).join(" ");' +
      '      return "<svg viewBox=\\"0 0 "+w+" "+h+"\\" width=\\""+w+"\\" height=\\""+h+"\\" preserveAspectRatio=\\"none\\" style=\\"vertical-align:middle\\"><polyline fill=\\"none\\" stroke=\\"#4a90e2\\" stroke-width=\\"1.2\\" points=\\""+pts+"\\"/></svg>";' +
      '    }' +
      '    var h="<table><tr><th>Metric</th><th style=\\"text-align:right\\">7d</th><th style=\\"text-align:right\\">30d</th><th style=\\"text-align:right\\">All</th><th>Trend (last "+recent.length+"d)</th></tr>";' +
      '    metrics.forEach(function(m){' +
      '      var all=rows.reduce(function(a,r){return a+(Number(r[m.key])||0);},0);' +
      '      var d7=sumLast(recent,m.key,7);' +
      '      var d30=sumLast(recent,m.key,30);' +
      '      h+="<tr><td><strong>"+m.label+"</strong></td><td style=\\"text-align:right\\">"+d7+"</td><td style=\\"text-align:right\\">"+d30+"</td><td style=\\"text-align:right\\">"+all+"</td><td>"+spark(recent,m.key)+"</td></tr>";' +
      '    });' +
      '    h+="</table>";' +
      '    h+="<h4 style=\\"margin:18px 0 8px;font-size:13px\\">Daily breakdown ("+rows.length+" days)</h4>";' +
      '    var cols=Object.keys(rows[0]);' +
      '    h+="<table><tr>";' +
      '    cols.forEach(function(c){h+="<th"+(c==="activity_date"?"":" style=\\"text-align:right\\"")+">"+c+"</th>";});' +
      '    h+="</tr>";' +
      '    rows.slice().reverse().forEach(function(r){' +
      '      h+="<tr>";' +
      '      cols.forEach(function(c){h+="<td"+(c==="activity_date"?"":" style=\\"text-align:right\\"")+">"+r[c]+"</td>";});' +
      '      h+="</tr>";' +
      '    });' +
      '    h+="</table>";' +
      '    el.innerHTML=h;' +
      '  }).catch(function(e){el.innerHTML="<div class=\\"no-data\\">Error: "+e.message+"</div>";});' +
      '}' +
      'function loadSvy(){' +
      '  var el=document.getElementById("svy");' +
      '  Promise.all([' +
      '    fetch("' + apiUrl + '/admin/views/survey_responses_with_user",{credentials:"include"}).then(function(r){if(!r.ok)throw new Error("HTTP "+r.status);return r.json()}),' +
      '    fetch("' + apiUrl + '/admin/views/users_analysis",{credentials:"include"}).then(function(r){if(!r.ok)throw new Error("HTTP "+r.status);return r.json()})' +
      '  ]).then(function(res){' +
      '    var responses=res[0]||[],users=res[1]||[];' +
      '    var total=users.length,resp=responses.length;' +
      '    var pct=total>0?Math.round(resp*100/total):0;' +
      '    var questions=[' +
      '      {key:"q1",label:"1. What were you trying to do when you signed up?",multi:false},' +
      '      {key:"q2",label:"2. Did the product solve your problem?",multi:false},' +
      '      {key:"q3",label:"3. Why haven\'t you come back?",multi:true},' +
      '      {key:"q4",label:"4. What kind of documents do you mostly work with?",multi:false},' +
      '      {key:"q5",label:"5. If we built something specifically for your use case, would you pay for it?",multi:false}' +
      '    ];' +
      '    var h="<div style=\\"font-size:15px;margin-bottom:14px\\"><strong>"+resp+"</strong> of <strong>"+total+"</strong> users responded ("+pct+"%)</div>";' +
      '    if(resp>0){' +
      '      h+="<h4 style=\\"margin:16px 0 8px;font-size:13px\\">Aggregated answers</h4>";' +
      '      questions.forEach(function(q){' +
      '        var counts={};' +
      '        responses.forEach(function(r){' +
      '          var ans=(r.answers&&r.answers[q.key])||(q.multi?[]:"");' +
      '          var arr=q.multi?(Array.isArray(ans)?ans:[]):(ans?[ans]:[]);' +
      '          arr.forEach(function(v){counts[v]=(counts[v]||0)+1;});' +
      '        });' +
      '        var keys=Object.keys(counts).sort(function(a,b){return counts[b]-counts[a];});' +
      '        h+="<div style=\\"margin-bottom:14px\\"><div style=\\"font-weight:600;font-size:13px;margin-bottom:4px\\">"+q.label+"</div>";' +
      '        if(!keys.length){h+="<div style=\\"font-size:12px;color:#888\\">(no answers)</div>";}' +
      '        else{' +
      '          h+="<table style=\\"width:100%\\"><tbody>";' +
      '          keys.forEach(function(k){' +
      '            var pct2=Math.round(counts[k]*100/resp);' +
      '            h+="<tr><td style=\\"padding:2px 6px\\">"+k+"</td><td style=\\"padding:2px 6px;text-align:right;width:70px\\">"+counts[k]+" ("+pct2+"%)</td></tr>";' +
      '          });' +
      '          h+="</tbody></table>";' +
      '        }' +
      '        h+="</div>";' +
      '      });' +
      '      h+="<h4 style=\\"margin:18px 0 8px;font-size:13px\\">Per-respondent details</h4>";' +
      '      h+="<table><tr><th>Username</th><th>Email</th><th>Submitted</th><th>Answers</th></tr>";' +
      '      responses.forEach(function(r){' +
      '        var d=fmtDate(r.submitted_at);' +
      '        var json=JSON.stringify(r.answers,null,2).replace(/</g,"&lt;");' +
      '        h+="<tr><td>"+(r.username||"-")+"</td><td>"+(r.email||"-")+"</td><td>"+d+"</td><td><details><summary>view</summary><pre style=\\"font-size:11px;white-space:pre-wrap\\">"+json+"</pre></details></td></tr>";' +
      '      });' +
      '      h+="</table>";' +
      '    } else {' +
      '      h+="<div class=\\"no-data\\">No survey responses yet.</div>";' +
      '    }' +
      '    el.innerHTML=h;' +
      '  }).catch(function(e){el.innerHTML="<div class=\\"no-data\\">Error: "+e.message+"</div>";});' +
      '}' : '') +
      'if(window.opener&&window.opener.ddTheme&&window.opener.ddTheme.get()==="dark"){document.body.classList.add("dark");}' +
      'loadTxn();loadEvt();loadSec();' + (orgGroup ? 'loadOrg();' : '') + (isDev ? 'loadUsr();loadAct();loadSvy();' : '') +
      '</script>' +
      '</body></html>'
    );
    w.document.close();
  }


  showContact(): void {
    this.showUserMenu = false;
    const t = (key: string) => this.translate.instant(key);
    const popW = 360;
    const popH = 220;
    const left = Math.round((screen.width - popW) / 2);
    const top = Math.round((screen.height - popH) / 2);
    const w = this.openPopup('contact', `width=${popW},height=${popH},left=${left},top=${top},scrollbars=no,resizable=yes`);
    if (!w) return;
    w.document.open();
    w.document.write(
      '<!DOCTYPE html><html><head><title>' + t('app.contact') + '</title>' +
      '<style>body{font-family:system-ui,sans-serif;margin:0;display:flex;align-items:center;justify-content:center;height:100vh;background:#f8f9fa}' +
      '.card{text-align:center;padding:2rem}.card h2{margin:0 0 1.5rem;font-size:1.2rem;color:#333}' +
      '.btn{display:inline-block;margin:0.5rem;padding:0.7rem 1.5rem;border-radius:6px;text-decoration:none;font-size:0.95rem;font-weight:500;cursor:pointer;border:none}' +
      '.btn-form{background:#4a90d9;color:#fff}.btn-form:hover{background:#357abd}' +
      '.btn-email{background:#fff;color:#333;border:1px solid #ccc}.btn-email:hover{background:#f0f0f0}' +
      '</style></head><body>' +
      '<div class="card">' +
      '<h2>' + t('contact.title') + '</h2>' +
      '<a class="btn btn-form" href="https://www.irisel.com/contact.php" target="_blank">' + t('contact.form') + '</a><br>' +
      '<a class="btn btn-email" href="mailto:holmes@irisel.com">' + t('contact.email') + '</a>' +
      '</div></body></html>'
    );
    w.document.close();
  }

  validateHelpEmail(): void {
    const email = this.helpEmail.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.helpEmailWarning = '';
    } else if (email) {
      this.helpEmailWarning = this.translate.instant('help.emailInvalid');
    } else {
      this.helpEmailWarning = '';
    }
  }

  toggleHelp(): void {
    this.showHelpDialog = !this.showHelpDialog;
    if (this.showHelpDialog) {
      this.helpStatus = 'idle';
      this.helpError = '';
      this.helpEmailWarning = '';
    }
  }

  sendHelp(): void {
    const email = this.helpEmail.trim();
    if (!email && !this.helpMessage.trim()) {
      this.helpError = this.translate.instant('help.emailRequired');
      return;
    }
    this.helpSending = true;
    this.helpError = '';
    this.validateHelpEmail();
    this.http.post(`${this.apiUrl}/help`, {
      email: email,
      message: this.helpMessage.trim()
    }).subscribe({
      next: () => {
        this.helpSending = false;
        this.helpStatus = 'success';
        this.helpEmail = '';
        this.helpMessage = '';
        if (typeof gtag === 'function') {
          gtag('event', 'generate_lead', { event_category: 'engagement', event_label: 'help_request' });
        }
      },
      error: () => {
        this.helpSending = false;
        this.helpError = this.translate.instant('help.error');
      }
    });
  }

  showAbout(): void {
      this.showUserMenu = false;
      const t = (key: string) => this.translate.instant(key);
      const lang = this.translate.currentLang || 'en';
      const popW = 420;
      const popH = 420;
      const left = Math.round((screen.width - popW) / 2);
      const top = Math.round((screen.height - popH) / 2);
      const w = this.openPopup('about', `width=${popW},height=${popH},left=${left},top=${top},scrollbars=no,resizable=no`);
      if (!w) return;

      // Build initial About HTML (help links loaded async)
      const baseHtml =
        '<!DOCTYPE html><html><head><title>' + t('about.title') + '</title>' +
        '<style>' +
        'body{font-family:Roboto,"Helvetica Neue",sans-serif;padding:32px;background:#fafafa;color:#333;margin:0;text-align:center;}' +
        '.logo{height:48px;margin-bottom:16px;background:white;border-radius:8px;padding:4px 10px;}' +
        'h2{font-size:18px;margin:0 0 8px;color:#333;font-weight:500;}' +
        '.version{font-size:14px;color:#666;margin:0 0 20px;}' +
        '.copyright{font-size:13px;color:#888;margin:16px 0 12px;}' +
        '.links{font-size:13px;margin:4px 0;}' +
        '.links a{color:#3f51b5;text-decoration:none;}' +
        '.links a:hover{text-decoration:underline;}' +
        '#help-links{margin-top:8px;}' +
        '</style></head><body>' +
        '<img src="' + this.safeUrl(this.headerLogoUrl) + '" alt="" class="logo" />' +
        '<h2>' + this.escapeHtml(this.brandProductName || t('app.title')) + '</h2>' +
        '<p class="version">' + t('about.version') + ' ' + this.appVersion + ' <span style="font-size:11px;color:#aaa;font-family:monospace;">(' + this.gitCommit + ')</span></p>' +
        '<p class="copyright">\u00a9 ' + new Date().getFullYear() + ' ' + this.escapeHtml(this.brandLegalName) + '</p>' +
        '<p class="links"><a href="' + this.safeUrl(this.brandTermsUrl) + '" target="_blank" rel="noopener noreferrer">' + t('about.terms') + '</a></p>' +
        '<div id="help-links"></div>' +
        '</body></html>';

      w.document.write(baseHtml);
      w.document.close();

      // Load help index and add links dynamically
      this.http.get<Array<{ file: string; title: string; title_es?: string }>>('assets/help/index.json')
        .subscribe({
          next: (pages) => {
            if (!pages || !pages.length || w.closed) return;
            const container = w.document.getElementById('help-links');
            if (!container) return;
            pages.forEach(page => {
              const label = (lang === 'es' && page.title_es) ? page.title_es : page.title;
              const p = w.document.createElement('p');
              p.className = 'links';
              const a = w.document.createElement('a');
              a.href = '#';
              a.textContent = '\ud83d\udcc4 ' + label;
              a.addEventListener('click', (e: Event) => {
                e.preventDefault();
                this.showHelpPage(page.file, label);
              });
              p.appendChild(a);
              container.appendChild(p);
            });
          },
          error: () => { /* help index not available — About popup still works */ }
        });
    }

  /** Open a help page (.md) from assets/help/ in a styled popup */
  private showHelpPage(file: string, title: string): void {
    this.http.get('assets/help/' + file, { responseType: 'text' }).subscribe({
      next: (md) => {
        const html = this.renderMarkdown(md);
        const popW = 900;
        const popH = 800;
        const left = Math.round((screen.width - popW) / 2);
        const top = Math.round((screen.height - popH) / 2);
        const w = this.openPopup('helppage', `width=${popW},height=${popH},left=${left},top=${top},scrollbars=yes,resizable=yes`);
        if (!w) return;
        w.document.write(
          '<!DOCTYPE html><html><head><title>' + this.escapeHtml(title) + '</title>' +
          '<style>' +
          'body{font-family:Roboto,"Helvetica Neue",sans-serif;margin:0;padding:32px 48px;background:#fafafa;color:#333;line-height:1.6;}' +
          'h1{font-size:24px;font-weight:500;color:#333;margin:0 0 4px;}' +
          'h2{font-size:20px;font-weight:500;color:#333;margin:28px 0 12px;padding-bottom:6px;border-bottom:2px solid #3f51b5;}' +
          'h3{font-size:16px;font-weight:600;color:#555;margin:20px 0 8px;}' +
          'p{margin:8px 0;font-size:14px;}' +
          'ul,ol{margin:8px 0;padding-left:24px;font-size:14px;}' +
          'li{margin:4px 0;}' +
          'strong{font-weight:600;}' +
          'em{font-style:italic;}' +
          'code{background:#f0f0f0;padding:2px 6px;border-radius:3px;font-family:Monaco,Menlo,"Ubuntu Mono",monospace;font-size:13px;}' +
          'pre{background:#f5f5f5;padding:16px;border-radius:4px;overflow-x:auto;font-size:13px;font-family:Monaco,Menlo,"Ubuntu Mono",monospace;border:1px solid #e0e0e0;}' +
          'pre code{background:none;padding:0;}' +
          'table{border-collapse:collapse;width:100%;margin:16px 0;font-size:14px;}' +
          'th{background:#3f51b5;color:white;padding:10px 14px;text-align:left;font-weight:500;}' +
          'td{padding:8px 14px;border-bottom:1px solid #e0e0e0;}' +
          'tr:hover td{background:#f5f5f5;}' +
          'hr{border:none;border-top:1px solid #e0e0e0;margin:24px 0;}' +
          'a{color:#3f51b5;text-decoration:none;}' +
          'a:hover{text-decoration:underline;}' +
          '</style></head><body>' + html + '</body></html>'
        );
        w.document.close();
      },
      error: () => console.warn('Help page not found: ' + file)
    });
  }

  /** Minimal markdown to HTML renderer — handles the subset used in help pages */
  private renderMarkdown(md: string): string {
    const lines = md.split('\n');
    let html = '';
    let inList = '';  // 'ul' | 'ol' | ''
    let inCode = false;
    let inTable = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Fenced code blocks
      if (line.trimStart().startsWith('```')) {
        if (inCode) {
          html += '</code></pre>';
          inCode = false;
        } else {
          html = this.closeOpenBlocks(html, inList, inTable);
          inList = '';
          inTable = false;
          html += '<pre><code>';
          inCode = true;
        }
        continue;
      }
      if (inCode) {
        html += this.escapeHtml(line) + '\n';
        continue;
      }

      // Blank line — close lists/tables
      if (line.trim() === '') {
        html = this.closeOpenBlocks(html, inList, inTable);
        inList = '';
        inTable = false;
        continue;
      }

      // Horizontal rule
      if (/^---+$/.test(line.trim())) {
        html = this.closeOpenBlocks(html, inList, inTable);
        inList = '';
        inTable = false;
        html += '<hr>';
        continue;
      }

      // Headers
      const hMatch = line.match(/^(#{1,6})\s+(.+)/);
      if (hMatch) {
        html = this.closeOpenBlocks(html, inList, inTable);
        inList = '';
        inTable = false;
        const level = hMatch[1].length;
        html += '<h' + level + '>' + this.inlineFormat(hMatch[2]) + '</h' + level + '>';
        continue;
      }

      // Table rows
      if (line.includes('|') && line.trim().startsWith('|')) {
        // Skip separator rows like |---|---|
        if (/^\|[\s\-:|]+\|$/.test(line.trim())) continue;
        if (!inTable) {
          html = this.closeOpenBlocks(html, inList, false);
          inList = '';
          html += '<table>';
          inTable = true;
          // First row is header
          const cells = line.split('|').filter(c => c.trim() !== '');
          html += '<tr>' + cells.map(c => '<th>' + this.inlineFormat(c.trim()) + '</th>').join('') + '</tr>';
          // Skip separator line if next
          if (i + 1 < lines.length && /^\|[\s\-:|]+\|$/.test(lines[i + 1].trim())) i++;
          continue;
        }
        const cells = line.split('|').filter(c => c.trim() !== '');
        html += '<tr>' + cells.map(c => '<td>' + this.inlineFormat(c.trim()) + '</td>').join('') + '</tr>';
        continue;
      }

      // Unordered list
      if (/^[-*]\s+/.test(line.trim())) {
        if (inTable) { html += '</table>'; inTable = false; }
        if (inList !== 'ul') {
          if (inList) html += '</' + inList + '>';
          html += '<ul>';
          inList = 'ul';
        }
        html += '<li>' + this.inlineFormat(line.trim().replace(/^[-*]\s+/, '')) + '</li>';
        continue;
      }

      // Ordered list
      const olMatch = line.trim().match(/^(\d+)\.\s+(.+)/);
      if (olMatch) {
        if (inTable) { html += '</table>'; inTable = false; }
        if (inList !== 'ol') {
          if (inList) html += '</' + inList + '>';
          html += '<ol>';
          inList = 'ol';
        }
        html += '<li>' + this.inlineFormat(olMatch[2]) + '</li>';
        continue;
      }

      // Paragraph
      html = this.closeOpenBlocks(html, inList, inTable);
      inList = '';
      inTable = false;
      html += '<p>' + this.inlineFormat(line) + '</p>';
    }

    // Close any remaining open blocks
    html = this.closeOpenBlocks(html, inList, inTable);
    if (inCode) html += '</code></pre>';
    return html;
  }

  private closeOpenBlocks(html: string, inList: string, inTable: boolean): string {
    if (inList) html += '</' + inList + '>';
    if (inTable) html += '</table>';
    return html;
  }

  /** Inline markdown formatting: bold, italic, code, links */
  private inlineFormat(text: string): string {
    let s = this.escapeHtml(text);
    // Inline code (before bold/italic to avoid conflicts)
    s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
    // Bold + italic
    s = s.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
    // Bold
    s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Italic
    s = s.replace(/\*(.+?)\*/g, '<em>$1</em>');
    // Links [text](url)
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');
    return s;
  }

  /** Escape a URL for an href/src built by string concatenation.
   *  Rejects anything that is not http(s) or a same-origin path: branding URLs
   *  are set by a tenant admin, and a 'javascript:' href would otherwise run in
   *  every user's About dialog. Escaping quotes alone would not prevent that. */
  private safeUrl(url: string): string {
    if (!url) return '';
    const isRelative = url.startsWith('/');
    if (!isRelative) {
      try {
        const scheme = new URL(url).protocol;
        if (scheme !== 'http:' && scheme !== 'https:') return '';
      } catch {
        return '';
      }
    }
    return this.escapeHtml(url);
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }



  isArray(value: any): boolean { return _isArray(value); }

  isObject(value: any): boolean {
    return _isObject(value);
  }

  getObjectKeys(obj: any): string[] {
    return _getObjectKeys(obj);
  }

  formatValue(value: any): string {
    return _formatValue(value);
  }
}
